import { TColorStyles } from "./css/color.css"
import { TSizeStyles } from "./css/size.css"
import { TTransformStyles } from "./css/transform.css"
import { TAnimationStyles } from "./css/animations.css"
import { Box, BoxOwnProps } from "./Box"
import type { ElementType, ForwardedRef } from "react"
import { forwardRef, useId } from "react"
import type {
	PolymorphicForwardRefExoticComponent,
	PolymorphicPropsWithoutRef,
	PolymorphicPropsWithRef,
} from "./polymorphic-types"
import {
	newBadgeStarGlow,
	newBadgeMainStar,
	newBadgeRays,
	newBadgeSparkleLeft,
	newBadgeSparkleRight,
	newBadgeShine,
	checkmark,
	checkmarkCircle,
	checkmarkCheck,
} from "./icons.css"

const DefaultIconElement = "svg"

export type IconOwnProps = { title?: string; spin?: boolean } & TSizeStyles &
	TColorStyles &
	Omit<TTransformStyles, "transform"> &
	TAnimationStyles &
	Pick<BoxOwnProps, "opacity" | "transition">

export type IconProps<T extends React.ElementType = typeof DefaultIconElement> =
	PolymorphicPropsWithRef<IconOwnProps, T>

export const Icon: PolymorphicForwardRefExoticComponent<
	IconOwnProps,
	typeof DefaultIconElement
> = forwardRef(function Text<T extends ElementType = typeof DefaultIconElement>(
	{
		as,
		title,
		children,
		size = "4",
		spin,
		...restProps
	}: PolymorphicPropsWithoutRef<IconOwnProps, T>,
	ref: ForwardedRef<Element>
) {
	const Element: ElementType = as || DefaultIconElement
	return (
		<Box
			animation={spin ? "spin" : undefined}
			{...restProps}
			as={Element}
			ref={ref}
			xmlns="http://www.w3.org/2000/svg"
			display="inlineBlock"
			size={size}
		>
			{title ? <title>{title}</title> : null}
			{children}
		</Box>
	)
})

export function AirplaneIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="currentColor">
			<path d="M16.5 14.25l-3.214-3.214L21 5.25 18.75 3 9.108 6.858 6.072 3.822c-.875-.875-2.097-1.085-2.715-.466-.62.619-.41 1.841.465 2.716l3.036 3.035L3 18.75 5.25 21l5.786-7.715L14.25 16.5V21h2.25l1.125-3.376L21 16.498v-2.25h-4.5v.001z" />
		</Icon>
	)
}

export function AnnotationIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
			/>
		</Icon>
	)
}

export function ArrowExpandIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
			/>
		</Icon>
	)
}

export function AttentionIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
		</Icon>
	)
}

export function AttentionSolidIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 20 20" fill="currentColor">
			<path
				d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
				clipRule="evenodd"
				fillRule="evenodd"
			/>
		</Icon>
	)
}

export function BadgeCheckSolidIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 20 20" fill="currentColor">
			<path
				d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
				clipRule="evenodd"
				fillRule="evenodd"
			/>
		</Icon>
	)
}

export function BaggageIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			strokeWidth={2}
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M22 18H6a2 2 0 0 1-2-2V7a2 2 0 0 0-2-2" />
			<path d="M17 14V4a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v10" />
			<rect width="13" height="8" x="8" y="6" rx="1" />
			<circle cx="18" cy="20" r="2" />
			<circle cx="9" cy="20" r="2" />
		</Icon>
	)
}
export function BanIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			strokeWidth={1.5}
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
			/>
		</Icon>
	)
}

export function BedIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="currentColor">
			<path d="M5.252 12.742h15.181a.51.51 0 01.394.173.64.64 0 01.173.393v3.937h-2.252v-2.252H5.252v2.252H3V6.568a.56.56 0 01.173-.41.49.49 0 01.394-.157h1.118c.158 0 .289.053.394.158a.61.61 0 01.173.41v6.173zm5.055-2.804c0-.62-.22-1.15-.661-1.59a2.169 2.169 0 00-1.59-.662c-.62 0-1.15.22-1.591.662a2.11 2.11 0 00-.646 1.59c.01.62.226 1.15.646 1.59.42.442.95.662 1.59.662.64 0 1.17-.22 1.59-.661.42-.441.641-.971.662-1.59zM21 12.19v-.567c0-.934-.33-1.732-.992-2.393-.662-.662-1.454-.987-2.378-.977h-6.19a.534.534 0 00-.393.158.593.593 0 00-.173.393v3.386H21z" />
		</Icon>
	)
}

export function BriefcaseIcons(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path d="M10 20v2" />
			<path d="M14 20v2" />
			<path d="M18 20v2" />
			<path d="M21 20H3" />
			<path d="M6 20v2" />
			<path d="M8 16V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v12" />
			<rect x="4" y="6" width="16" height="10" rx="2" />
		</Icon>
	)
}
export function BuildingIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
			<path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
			<path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
			<path d="M10 6h4" />
			<path d="M10 10h4" />
			<path d="M10 14h4" />
			<path d="M10 18h4" />
		</Icon>
	)
}
export function BusIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="currentColor">
			<path d="M7.495 15.034c0-.354-.147-.656-.441-.904a1.576 1.576 0 00-1.05-.372c-.407 0-.764.124-1.072.372-.308.248-.455.55-.44.904.013.354.16.65.44.886s.637.36 1.071.372c.435.012.785-.112 1.05-.372.267-.26.414-.555.442-.886zm12.014 0c0-.354-.147-.656-.441-.904a1.606 1.606 0 00-1.072-.372c-.42 0-.77.124-1.05.372s-.427.55-.44.904c-.015.354.132.65.44.886s.658.36 1.05.372c.393.012.75-.112 1.072-.372.322-.26.469-.555.44-.886zm-.546-3.917l-.84-3.81a.644.644 0 00-.274-.372.809.809 0 00-.462-.142H6.613a.808.808 0 00-.462.142.644.644 0 00-.274.372l-.84 3.81c-.042.19.014.36.168.514a.77.77 0 00.567.23h12.456c.238 0 .427-.076.567-.23a.602.602 0 00.168-.514zm-2.647-6.06a.428.428 0 00-.168-.337.599.599 0 00-.399-.142H8.251a.599.599 0 00-.4.142.428.428 0 00-.167.337c0 .13.056.242.168.336a.599.599 0 00.399.142h7.498a.599.599 0 00.4-.142.428.428 0 00.167-.336zM21 12.234v5.954h-1.491v1.276c0 .343-.147.638-.441.887-.294.248-.651.372-1.072.372-.42 0-.77-.124-1.05-.372-.28-.249-.427-.544-.44-.887v-1.276H7.494v1.276c0 .343-.147.638-.441.887a1.576 1.576 0 01-1.05.372c-.407 0-.764-.124-1.072-.372-.308-.249-.455-.544-.44-.887v-1.276H3v-5.954c0-.744.098-1.483.294-2.215l1.197-4.484c.084-.52.47-.975 1.155-1.364.687-.39 1.583-.68 2.689-.869a23.015 23.015 0 013.675-.301c1.345-.012 2.563.089 3.655.301 1.092.213 1.988.502 2.689.869.7.366 1.085.82 1.155 1.364l1.218 4.484c.182.673.273 1.412.273 2.215z" />
		</Icon>
	)
}

export function CircleArrowIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path d="m9 12.75 3 3m0 0 3-3m-3 3v-7.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
		</Icon>
	)
}
export function CalendarIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</Icon>
	)
}

export function ClockIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
			/>
		</Icon>
	)
}
export function ClockSolidIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="currentColor">
			<path
				fillRule="evenodd"
				d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z"
				clipRule="evenodd"
			/>
		</Icon>
	)
}

// Close, Cross, X, Remove, Dismiss
export function CancelIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path d="M6 18L18 6M6 6l12 12" />
		</Icon>
	)
}

export function CancelCircleSolidIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 20 20" fill="currentColor">
			<path
				d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
				clipRule="evenodd"
				fillRule="evenodd"
			/>
		</Icon>
	)
}

export function ChevronDownIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path d="M19 9l-7 7-7-7" />
		</Icon>
	)
}

export function ChevronRightIcon(props: IconProps) {
	return <ChevronDownIcon {...props} rotate="270" />
}

export function ChevronLeftIcon(props: IconProps) {
	return <ChevronDownIcon {...props} rotate="90" />
}

export function ClipboardCopyIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
			/>
		</Icon>
	)
}

export function CogIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
			<path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
		</Icon>
	)
}

export function OkCircleSolidIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 20 20" fill="currentColor">
			<path
				d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
				clipRule="evenodd"
				fillRule="evenodd"
			/>
		</Icon>
	)
}
export function OkCircleIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			strokeWidth={1.5}
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
			/>
		</Icon>
	)
}
export function XCircleIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			strokeWidth={1.5}
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
			/>
		</Icon>
	)
}

export function InfoSolidIcon(props: IconProps) {
	return (
		<Icon {...props} fill="currentColor" viewBox="0 0 20 20">
			<path
				d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
				clipRule="evenodd"
				fillRule="evenodd"
			/>
		</Icon>
	)
}

export function RupeeIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M6 5h12M8 5a4.8 4.8 0 013.6 1.5A5.1 5.1 0 0113 10m0 0a5.1 5.1 0 01-3 4.6c-.5.3-1.4.4-2 .4H6l6 6m1-11H6m7 0h5" />
		</Icon>
	)
}

export function DocumentIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
		</Icon>
	)
}

export function DocumentDownloadIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
		</Icon>
	)
}

export function PaperClipIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={1.5}
			viewBox="0 0 24 24"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
			/>
		</Icon>
	)
}

export function DotsVerticalIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
		</Icon>
	)
}

export function DuplicateIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
		</Icon>
	)
}

export function DriverIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M7.05 16.05a7 7 0 019.9 0m-9.9 0A7 7 0 005 21h7m-4.95-4.95L12 21m4.95-4.95A7 7 0 0119 21h-7m4.95-4.95L12 21m4-14a4 4 0 11-8 0 4 4 0 018 0z" />
		</Icon>
	)
}

export function SteeringWheelIcon(props: IconProps) {
	return (
		<Icon {...props} fill="currentColor" viewBox="0 0 24 24">
			<path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 2a8 8 0 0 1 7.38 4.92 29.93 29.93 0 0 0-14.78.02A8 8 0 0 1 12 4Zm-8 8.67 1.11-.13A4.38 4.38 0 0 1 10 16.89v2.85a8 8 0 0 1-6-7.07Zm10 7.07v-2.85a4.38 4.38 0 0 1 4.86-4.35l1.11.13A8 8 0 0 1 14 19.74Z" />
		</Icon>
	)
}

export function FlagIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
		</Icon>
	)
}

export function FlagSolidIcon(props: IconProps) {
	return (
		<Icon {...props} fill="currentColor" viewBox="0 0 20 20">
			<path
				fillRule="evenodd"
				d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z"
				clipRule="evenodd"
			/>
		</Icon>
	)
}
export function GlobalIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<circle cx="12" cy="12" r="10" />
			<path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
			<path d="M2 12h20" />
		</Icon>
	)
}
export function HandPencilIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			viewBox="0 0 64 64"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={4}
		>
			<path d="m13.62 38.11 14.36-29.35c.62-1.18 2.08-1.64 3.26-1.02l2.44 1.28c1.18.62 1.63 2.08 1.01 3.26l-12.63 25.83" />
			<path d="m10.53 44.41-2.27 4.65 6.72 3.53 3.89-7.95" />
			<path d="m34.04 13.52-6.68-3.5" />
			<path d="m14.97 52.59-6.35 3.95-.36-7.48" />
			<path d="m29.1 23.7 2.33 1.02c.82.36 1.53.9 2.1 1.59l3.54 4.34c1.06 1.3 2.64 2.05 4.31 2.05h2.83" />
			<path d="m27.36 38.11h-16.36c-1.74 0-3.15 1.41-3.15 3.15v.04c0 1.81 1.52 3.24 3.33 3.14 3.18-.16 7.58-.36 9.92.89 4.94 2.64 5.91 4.8 12.32 4.8h11.28" />
			<path d="m21.58 21.82-2.91-.05c-2.32 0-4.43 1.38-5.35 3.51l-8.66 15.53c-1.11 2.27-.13 5.01 2.17 6.07l2.03.94" />
			<path d="m56.32 51.74h-8.71c-1.92 0-3.47-1.55-3.47-3.47v-14.58c0-1.92 1.55-3.47 3.47-3.47h8.71c1.92 0 3.47 1.55 3.47 3.47v14.58c.01 1.91-1.55 3.47-3.47 3.47z" />
			<path d="m44.21 40.98h7.76" />
		</Icon>
	)
}
export function HandShakeIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		>
			<path d="m11 17 2 2a1 1 0 1 0 3-3" />
			<path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
			<path d="m21 3 1 11h-2" />
			<path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
			<path d="M3 4h8" />
		</Icon>
	)
}
export function HomeIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
		</Icon>
	)
}

export function IdentificationIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
		</Icon>
	)
}

export function InfoIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
		</Icon>
	)
}

export function LightBulbIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
		</Icon>
	)
}

export function LightningBoltIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M13 10V3L4 14h7v7l9-11h-7z" />
		</Icon>
	)
}

export function LightningBoltSolidIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 20 20"
			fill="currentColor"
			fillRule="evenodd"
			clipRule="evenodd"
		>
			<path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" />
		</Icon>
	)
}

export function LocationMarkerIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
			<path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
		</Icon>
	)
}

export function LoginIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
		</Icon>
	)
}

export function LogoutIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
		</Icon>
	)
}

export function MailIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
		</Icon>
	)
}

export function MapIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
		</Icon>
	)
}

export function MealIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8" />
			<path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7" />
			<path d="m2.1 21.8 6.4-6.3" />
			<path d="m19 5-7 7" />
		</Icon>
	)
}
export function MinusCircleIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
		</Icon>
	)
}

// CheckIcon, CorrectIcon, TickIcon, Confirm, Done, Approve
export function OkIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M5 13l4 4L19 7" />
		</Icon>
	)
}

export function CheckCheckIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M18 6 7 17l-5-5" />
			<path d="m22 10-7.5 7.5L13 16" />
		</Icon>
	)
}

export function PaperAirplaneIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
		</Icon>
	)
}

export function PencilIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
		</Icon>
	)
}

export function PhoneIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
		</Icon>
	)
}

export function MobileIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"
			/>
		</Icon>
	)
}

export function PhotographIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</Icon>
	)
}

export function PlusIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
		</Icon>
	)
}

export function PlusCircleIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
		</Icon>
	)
}

export function QuestionMarkCircleIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
		</Icon>
	)
}

export function RefreshIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
		</Icon>
	)
}

export function ReplyIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
		</Icon>
	)
}

export function SearchIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
		</Icon>
	)
}

export function ServiceIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="currentColor"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			viewBox="0 0 512 512"
		>
			<path d="m0 512v-157.406h84.8v157.406zm321.036-20.712c-3.424 1.893-7.524 2.034-12.9.459-72.727-21.36-125.909-12.472-168.643-5.314-14.855 2.478-29 4.84-42.4 5.681v-120.474a191.892 191.892 0 0 0 37.256-27.74 26.583 26.583 0 0 1 12.142-6.83 87.75 87.75 0 0 1 46.439.338c59.971 25.437 105 45.937 133.837 60.93 6.973 3.623 7.451 10.4 5.574 15.51-2.459 6.709-9.525 12.443-19.765 10.12-11.272-2.56-21.827-5.2-32.035-7.753-37.942-9.5-67.909-17.007-95.119-10.095a6.142 6.142 0 1 0 3.024 11.906c24.207-6.144 52.849 1.024 89.11 10.11 10.274 2.57 20.9 5.232 32.3 7.811a33.137 33.137 0 0 0 7.37.85 28.333 28.333 0 0 0 26.649-18.718c2.71-7.395 2.15-14.746-1.137-20.736 29.2-9.448 72.658-18.8 129.423-27.842 8.9-1.41 16 4.473 19.442 8.12 5.714 6.043 7.912 12.37 8.106 14.848l-178.673 98.818zm-267.15-123.988a18.254 18.254 0 1 0 18.254 18.256 18.278 18.278 0 0 0 -18.254-18.256zm0 24.219a5.966 5.966 0 1 0 -5.964-5.961 5.968 5.968 0 0 0 5.964 5.961zm445.7-249.312-31.451-3.758a140.821 140.821 0 0 0 -10.725-25.859l19.59-24.914a14.092 14.092 0 0 0 -1.115-18.669l-32.885-32.89a14.094 14.094 0 0 0 -18.674-1.117l-24.919 19.6a140.336 140.336 0 0 0 -25.848-10.724l-3.759-31.466a14.089 14.089 0 0 0 -13.991-12.41h-46.516a14.091 14.091 0 0 0 -13.987 12.41l-3.762 31.469a140.172 140.172 0 0 0 -25.852 10.721l-24.916-19.6a14.1 14.1 0 0 0 -18.668 1.116l-32.892 32.89a14.094 14.094 0 0 0 -1.116 18.67l19.6 24.914a140.8 140.8 0 0 0 -10.723 25.857l-31.457 3.758a14.1 14.1 0 0 0 -12.42 13.989v46.521a14.1 14.1 0 0 0 12.418 13.985l31.451 3.758a140.869 140.869 0 0 0 10.723 25.857l-19.592 24.912a14.094 14.094 0 0 0 1.113 18.669l32.892 32.89a14.085 14.085 0 0 0 18.668 1.106l24.927-19.592a141.447 141.447 0 0 0 25.848 10.724l3.762 31.46a14.1 14.1 0 0 0 13.983 12.416h46.516a14.1 14.1 0 0 0 13.991-12.416l3.761-31.45a141.414 141.414 0 0 0 25.859-10.719l24.908 19.577a14.089 14.089 0 0 0 18.672-1.106l32.889-32.89a14.092 14.092 0 0 0 1.111-18.669l-19.6-24.927a141.08 141.08 0 0 0 10.721-25.847l31.461-3.758a14.1 14.1 0 0 0 12.418-13.98v-46.521a14.1 14.1 0 0 0 -12.414-13.989zm-167.034-45.141a82.381 82.381 0 1 1 -82.382 82.381 82.475 82.475 0 0 1 82.382-82.381zm0 152.473a70.093 70.093 0 1 1 70.092-70.092 70.167 70.167 0 0 1 -70.092 70.092z" />
		</Icon>
	)
}
export function ShareIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
		</Icon>
	)
}
export function SheetIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4" />
			<path d="M14 2v4a2 2 0 0 0 2 2h4" />
			<path d="M2 15h10" />
			<path d="m9 18 3-3-3-3" />
		</Icon>
	)
}

export function ShieldCheckIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
		</Icon>
	)
}

export function EmptyShieldIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
		</Icon>
	)
}

export function KeyRoundLuciode(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z"
			/>
		</Icon>
	)
}

export function ShieldExclamationIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
		</Icon>
	)
}

export function SlidersIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
		</Icon>
	)
}

export function HorizontalSlidersIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<path d="M21 4H14M10 4H3M21 12H12M8 12H3M21 20H16M12 20H3M14 2V6M8 10V14M16 18V22" />
		</Icon>
	)
}

export function SpinnerIcon(props: IconProps) {
	return (
		<Icon {...props} fill="currentColor" viewBox="0 0 24 24" animation="spin">
			<path d="M11.957 3c-2.05 0-4.101.702-5.756 2.088 3.112-2.52 7.699-2.34 10.576.558a7.916 7.916 0 011.601 8.912c-.18.522-.09.9.216 1.224.378.36 1.097.414 1.511 0 .054-.054.216-.27.27-.432a9.07 9.07 0 00-2.015-9.704A9.085 9.085 0 0011.957 3zM4.6 7.933a.95.95 0 00-.701.288c-.054.054-.234.27-.27.432a9.035 9.035 0 002.015 9.704c3.327 3.33 8.615 3.51 12.159.558-3.112 2.52-7.699 2.34-10.595-.558-2.41-2.413-2.931-5.995-1.6-8.912.197-.522.108-.9-.216-1.224a1.03 1.03 0 00-.792-.288z" />
		</Icon>
	)
}

export function StarIcon(props: IconProps) {
	return (
		<Icon {...props} fill="currentColor" viewBox="0 0 20 20">
			<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
		</Icon>
	)
}

export function StarEmptyIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
		</Icon>
	)
}

export function SupportIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
		</Icon>
	)
}

export function SwitchHorizontalIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
		</Icon>
	)
}

export function TagsIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
		</Icon>
	)
}

export function UploadIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
		</Icon>
	)
}

export function UserIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zm-4 7a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
		</Icon>
	)
}
export function AdultIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
			<circle cx="12" cy="7" r="4" />
		</Icon>
	)
}
export function UserRoundIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<circle cx="12" cy="8" r="5" />
			<path d="M20 21a8 8 0 0 0-16 0" />
		</Icon>
	)
}

export function TrashIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
		</Icon>
	)
}

export function UserGroupIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
		</Icon>
	)
}

export function UsersIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
		</Icon>
	)
}

export function ViewListIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			stroke="currentColor"
			viewBox="0 0 24 24"
		>
			<path d="M4 6h16M4 10h16M4 14h16M4 18h16" />
		</Icon>
	)
}

export function ArrowLeftIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
			<path
				d="M10 19l-7-7m0 0l7-7m-7 7h18"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function MarkdownIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 16 16">
			<path
				fillRule="evenodd"
				fill="currentColor"
				d="M14.85 3H1.15C.52 3 0 3.52 0 4.15v7.69C0 12.48.52 13 1.15 13h13.69c.64 0 1.15-.52 1.15-1.15v-7.7C16 3.52 15.48 3 14.85 3zM9 11H7V8L5.5 9.92 4 8v3H2V5h2l1.5 2L7 5h2v6zm2.99.5L9.5 8H11V5h2v3h1.5l-2.51 3.5z"
			></path>
		</Icon>
	)
}

export function InboxInIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={2}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20"
			/>
		</Icon>
	)
}

export function ArrowTopRightOnSquareIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
			/>
		</Icon>
	)
}

export function BellIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
			/>
		</Icon>
	)
}

export function BellSlashIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M9.143 17.082a24.248 24.248 0 0 0 3.844.148m-3.844-.148a23.856 23.856 0 0 1-5.455-1.31 8.964 8.964 0 0 0 2.3-5.542m3.155 6.852a3 3 0 0 0 5.667 1.97m1.965-2.277L21 21m-4.225-4.225a23.81 23.81 0 0 0 3.536-1.003A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6.53 6.53m10.245 10.245L6.53 6.53M3 3l3.53 3.53"
			/>
		</Icon>
	)
}

export function BellAlertSolidIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="currentColor">
			<path d="M5.85 3.5a.75.75 0 00-1.117-1 9.719 9.719 0 00-2.348 4.876.75.75 0 001.479.248A8.219 8.219 0 015.85 3.5zM19.267 2.5a.75.75 0 10-1.118 1 8.22 8.22 0 011.987 4.124.75.75 0 001.48-.248A9.72 9.72 0 0019.266 2.5z" />
			<path
				fillRule="evenodd"
				d="M12 2.25A6.75 6.75 0 005.25 9v.75a8.217 8.217 0 01-2.119 5.52.75.75 0 00.298 1.206c1.544.57 3.16.99 4.831 1.243a3.75 3.75 0 107.48 0 24.583 24.583 0 004.83-1.244.75.75 0 00.298-1.205 8.217 8.217 0 01-2.118-5.52V9A6.75 6.75 0 0012 2.25zM9.75 18c0-.034 0-.067.002-.1a25.05 25.05 0 004.496 0l.002.1a2.25 2.25 0 11-4.5 0z"
				clipRule="evenodd"
			/>
		</Icon>
	)
}

export function SpeakerWaveIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z"
			/>
		</Icon>
	)
}

export function SpeakerXMarkIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6 4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z"
			/>
		</Icon>
	)
}

export function BankNotesIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
			/>
		</Icon>
	)
}

export function LifeBouyIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M16.712 4.33a9.027 9.027 0 011.652 1.306c.51.51.944 1.064 1.306 1.652M16.712 4.33l-3.448 4.138m3.448-4.138a9.014 9.014 0 00-9.424 0M19.67 7.288l-4.138 3.448m4.138-3.448a9.014 9.014 0 010 9.424m-4.138-5.976a3.736 3.736 0 00-.88-1.388 3.737 3.737 0 00-1.388-.88m2.268 2.268a3.765 3.765 0 010 2.528m-2.268-4.796a3.765 3.765 0 00-2.528 0m4.796 4.796c-.181.506-.475.982-.88 1.388a3.736 3.736 0 01-1.388.88m2.268-2.268l4.138 3.448m0 0a9.027 9.027 0 01-1.306 1.652c-.51.51-1.064.944-1.652 1.306m0 0l-3.448-4.138m3.448 4.138a9.014 9.014 0 01-9.424 0m5.976-4.138a3.765 3.765 0 01-2.528 0m0 0a3.736 3.736 0 01-1.388-.88 3.737 3.737 0 01-.88-1.388m2.268 2.268L7.288 19.67m0 0a9.024 9.024 0 01-1.652-1.306 9.027 9.027 0 01-1.306-1.652m0 0l4.138-3.448M4.33 16.712a9.014 9.014 0 010-9.424m4.138 5.976a3.765 3.765 0 010-2.528m0 0c.181-.506.475-.982.88-1.388a3.736 3.736 0 011.388-.88m-2.268 2.268L4.33 7.288m6.406 1.18L7.288 4.33m0 0a9.024 9.024 0 00-1.652 1.306A9.025 9.025 0 004.33 7.288"
			/>
		</Icon>
	)
}

export function FingerPrintIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M7.864 4.243A7.5 7.5 0 0119.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 004.5 10.5a7.464 7.464 0 01-1.15 3.993m1.989 3.559A11.209 11.209 0 008.25 10.5a3.75 3.75 0 117.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 01-3.6 9.75m6.633-4.596a18.666 18.666 0 01-2.485 5.33"
			/>
		</Icon>
	)
}

export function ArrowDownLeftIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M19.5 4.5l-15 15m0 0h11.25m-11.25 0V8.25"
			/>
		</Icon>
	)
}

export function ArrowRightLeftIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path d="m16 3 4 4-4 4" />
			<path d="M20 7H4" />
			<path d="m8 21-4-4 4-4" />
			<path d="M4 17h16" />
		</Icon>
	)
}

export function SparklesSolidIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="currentColor">
			<path
				fillRule="evenodd"
				d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5zM18 1.5a.75.75 0 01.728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 010 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 01-1.456 0l-.258-1.036a2.625 2.625 0 00-1.91-1.91l-1.036-.258a.75.75 0 010-1.456l1.036-.258a2.625 2.625 0 001.91-1.91l.258-1.036A.75.75 0 0118 1.5zM16.5 15a.75.75 0 01.712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 010 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 01-1.422 0l-.395-1.183a1.5 1.5 0 00-.948-.948l-1.183-.395a.75.75 0 010-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0116.5 15z"
				clipRule="evenodd"
			/>
		</Icon>
	)
}

export function RectangleStackIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M6 6.878V6a2.25 2.25 0 0 1 2.25-2.25h7.5A2.25 2.25 0 0 1 18 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 0 0 4.5 9v.878m13.5-3A2.25 2.25 0 0 1 19.5 9v.878m0 0a2.246 2.246 0 0 0-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0 1 21 12v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6c0-.98.626-1.813 1.5-2.122"
			/>
		</Icon>
	)
}

export function TicketIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinejoin="round"
				strokeLinecap="round"
				d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z"
			/>
		</Icon>
	)
}

export function HandRaisedIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.5}
			fill="none"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M10.05 4.575a1.575 1.575 0 1 0-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 0 1 3.15 0v1.5m-3.15 0 .075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 0 1 3.15 0V15M6.9 7.575a1.575 1.575 0 1 0-3.15 0v8.175a6.75 6.75 0 0 0 6.75 6.75h2.018a5.25 5.25 0 0 0 3.712-1.538l1.732-1.732a5.25 5.25 0 0 0 1.538-3.712l.003-2.024a.668.668 0 0 1 .198-.471 1.575 1.575 0 1 0-2.228-2.228 3.818 3.818 0 0 0-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0 1 16.35 15m.002 0h-.002"
			/>
		</Icon>
	)
}

export function HandThumbUpIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			strokeWidth={1.5}
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z"
			/>
		</Icon>
	)
}

export function HandThumbUpSolidIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="currentColor">
			<path d="M7.493 18.5c-.425 0-.82-.236-.975-.632A7.48 7.48 0 0 1 6 15.125c0-1.75.599-3.358 1.602-4.634.151-.192.373-.309.6-.397.473-.183.89-.514 1.212-.924a9.042 9.042 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75A.75.75 0 0 1 15 2a2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H14.23c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23h-.777ZM2.331 10.727a11.969 11.969 0 0 0-.831 4.398 12 12 0 0 0 .52 3.507C2.28 19.482 3.105 20 3.994 20H4.9c.445 0 .72-.498.523-.898a8.963 8.963 0 0 1-.924-3.977c0-1.708.476-3.305 1.302-4.666.245-.403-.028-.959-.5-.959H4.25c-.832 0-1.612.453-1.918 1.227Z" />
		</Icon>
	)
}

export function TaxiIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.5}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M10 2h4" />
			<path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8" />
			<path d="M7 14h.01" />
			<path d="M17 14h.01" />
			<rect width="18" height="8" x="3" y="10" rx="2" />
			<path d="M5 18v2" />
			<path d="M19 18v2" />
		</Icon>
	)
}
export function TicketPlainIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.5}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M10 2h4" />
			<path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8" />
			<path d="M7 14h.01" />
			<path d="M17 14h.01" />
			<rect width="18" height="8" x="3" y="10" rx="2" />
			<path d="M5 18v2" />
			<path d="M19 18v2" />
		</Icon>
	)
}
export function RefreshClockIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
			<path d="M3 3v5h5" />
			<path d="M12 7v5l4 2" />
		</Icon>
	)
}
export function ReceiptIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 64 64"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M15.181 62L48.173 62C50.531 62 52.45 60.081 52.45 57.723L52.45 16.948L57.036 16.948C57.589 16.948 58.036 16.5 58.036 15.948L58.036 3C58.036 2.448 57.589 2 57.036 2L14.153 2C13.6 2 13.153 2.448 13.153 3L13.153 15.948C13.153 16.5 13.6 16.948 14.153 16.948L18.738 16.948L18.738 39.517L9.69901 39.517C8.70401 39.517 7.76301 39.908 7.04401 40.626C6.34801 41.34 5.96401 42.272 5.96401 43.252L5.96401 49.899C5.96401 50.451 6.41101 50.899 6.96401 50.899L11.434 50.899L11.434 58.253C11.434 60.319 13.115 62 15.181 62ZM50.45 57.723C50.45 58.979 49.429 60 48.173 60L20.307 60C20.582 59.478 20.739 58.884 20.739 58.253L20.739 40.521C20.739 40.521 20.739 40.518 20.739 40.516C20.739 40.514 20.739 40.513 20.739 40.511L20.739 10.473L50.45 10.473L50.45 57.723ZM15.153 14.948L15.153 4L56.036 4L56.036 14.948L52.45 14.948L52.45 9.474C52.45 8.922 52.003 8.474 51.45 8.474L19.738 8.474C19.185 8.474 18.738 8.922 18.738 9.474L18.738 14.948L15.153 14.948ZM18.738 58.254C18.738 59.217 17.96 60.001 17.004 60.001L15.181 60.001C14.218 60.001 13.434 59.217 13.434 58.254L13.434 43.252C13.434 42.622 13.263 42.038 12.987 41.517L18.739 41.517L18.739 58.253L18.738 58.254ZM7.96401 43.252C7.96401 42.797 8.14601 42.361 8.46601 42.033C8.79901 41.7 9.23701 41.517 9.69901 41.517C10.655 41.517 11.433 42.295 11.433 43.252L11.433 48.899H7.96301L7.96401 43.252Z" />
		</Icon>
	)
}

export function LockIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
			/>
		</Icon>
	)
}
export function UnLockIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
			/>
		</Icon>
	)
}

export function DocumentTextIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
			/>
		</Icon>
	)
}

export function PreviewIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.5}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
			/>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
			/>
		</Icon>
	)
}

export function LightFlagIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 115 12"
			fill="none"
			stroke="currentColor"
			strokeWidth={0.5}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<g clipPath="url(#clip0_516_22805)">
				<path d="M106.063 9.56998H105.928V9.36984C105.928 9.181 105.776 9.02832 105.587 9.02832H104.571C104.384 9.02832 104.23 9.18097 104.23 9.36984V9.56998H104.096C104.018 9.56998 103.955 9.6333 103.955 9.71133C103.955 9.78936 104.018 9.85268 104.096 9.85268H106.063C106.141 9.85268 106.204 9.78936 106.204 9.71133C106.204 9.6333 106.141 9.56998 106.063 9.56998ZM105.646 9.56998H104.513V9.36984C104.513 9.33703 104.54 9.31104 104.571 9.31104H105.587C105.62 9.31104 105.646 9.33705 105.646 9.36984V9.56998Z" />
				<path d="M114.727 9.56998H114.592V9.36984C114.592 9.181 114.44 9.02832 114.252 9.02832H113.235C113.048 9.02832 112.895 9.18097 112.895 9.36984V9.56998H112.76C112.682 9.56998 112.619 9.6333 112.619 9.71133C112.619 9.78936 112.682 9.85268 112.76 9.85268H114.727C114.805 9.85268 114.868 9.78936 114.868 9.71133C114.868 9.6333 114.805 9.56998 114.727 9.56998ZM114.31 9.56998H113.178V9.36984C113.178 9.33703 113.204 9.31104 113.235 9.31104H114.252C114.284 9.31104 114.31 9.33705 114.31 9.36984V9.56998Z" />
				<path d="M113.744 1.32227C113.503 1.32227 113.308 1.5179 113.308 1.75875V2.17375H109.412H105.516V1.75877C105.516 1.51226 105.315 1.32229 105.08 1.32229C104.84 1.32229 104.645 1.51792 104.645 1.75877V8.60473C104.645 8.68276 104.708 8.74608 104.786 8.74608C104.864 8.74608 104.927 8.68276 104.927 8.60473V1.75877C104.927 1.67395 104.995 1.60498 105.08 1.60498C105.161 1.60498 105.234 1.67057 105.234 1.75877V8.60473C105.234 8.68276 105.297 8.74608 105.375 8.74608C105.453 8.74608 105.516 8.68276 105.516 8.60473V5.7856H113.308V8.60471C113.308 8.68274 113.371 8.74606 113.449 8.74606C113.527 8.74606 113.59 8.68274 113.59 8.60471V1.75877C113.59 1.67395 113.659 1.60498 113.744 1.60498C113.828 1.60498 113.897 1.67395 113.897 1.75877V8.60473C113.897 8.68276 113.96 8.74608 114.038 8.74608C114.116 8.74608 114.179 8.68276 114.179 8.60473V1.75877C114.18 1.51903 113.986 1.32227 113.744 1.32227ZM106.354 4.41506H105.516V3.54432H106.354V4.41506ZM107.541 5.50288H106.67V4.66495H107.541V5.50288ZM107.541 3.29441H106.67V2.45647H107.541V3.29441ZM108.661 4.41506H107.79V3.54432H108.661V4.41506ZM109.847 5.50288H108.977V4.66495H109.847V5.50288ZM109.847 3.29441H108.977V2.45647H109.847V3.29441ZM110.968 4.41506H110.097V3.54432H110.968V4.41506ZM112.154 5.50288H111.284V4.66495H112.154V5.50288ZM112.154 3.29441H111.284V2.45647H112.154V3.29441ZM113.308 4.41506H112.404V3.54432H113.308V4.41506Z" />
			</g>
			<path d="M114.754 11V10.5H113.77V11V11.5H114.754V11ZM111.801 11V10.5L109.832 10.5V11V11.5L111.801 11.5V11ZM107.863 11V10.5H105.895V11V11.5H107.863V11ZM103.926 11V10.5H101.957V11V11.5H103.926V11ZM99.9886 11V10.5H98.0198V11V11.5H99.9886V11ZM96.0511 11V10.5H94.0824V11V11.5H96.0511V11ZM92.1137 11V10.5H90.145V11V11.5L92.1137 11.5V11ZM88.1763 11V10.5H86.2076V11V11.5H88.1763V11ZM84.2389 11V10.5L82.2702 10.5V11V11.5L84.2389 11.5V11ZM80.3014 11V10.5H78.3327V11V11.5H80.3014V11ZM76.364 11V10.5L74.3953 10.5V11V11.5L76.364 11.5V11ZM72.4266 11V10.5H70.4579V11V11.5H72.4266V11ZM68.4892 11V10.5H66.5205V11V11.5L68.4892 11.5V11ZM64.5517 11V10.5H62.583V11V11.5H64.5517V11ZM60.6143 11V10.5H58.6456V11V11.5H60.6143V11ZM56.6769 11V10.5H54.7082V11V11.5H56.6769V11ZM52.7395 11V10.5H50.7708V11V11.5H52.7395V11ZM48.802 11V10.5L46.8333 10.5V11V11.5L48.802 11.5V11ZM44.8646 11V10.5H42.8959V11V11.5H44.8646V11ZM40.9272 11V10.5L38.9585 10.5V11V11.5L40.9272 11.5V11ZM36.9898 11V10.5H35.0211V11V11.5H36.9898V11ZM33.0524 11V10.5H31.0836V11V11.5H33.0524V11ZM29.1149 11V10.5H27.1462V11V11.5H29.1149V11ZM25.1775 11V10.5H23.2088V11V11.5H25.1775V11ZM21.2401 11V10.5L19.2714 10.5V11V11.5L21.2401 11.5V11ZM17.3027 11V10.5H15.3339V11V11.5H17.3027V11ZM13.3652 11V10.5L11.3965 10.5V11V11.5L13.3652 11.5V11ZM9.42781 11V10.5H7.4591V11V11.5H9.42781V11ZM5.49039 11V10.5L4.50599 10.5V11V11.5L5.49039 11.5V11Z" />
			<g clipPath="url(#clip1_516_22805)">
				<path d="M1.22454 4.60514C1.23596 4.59289 1.25019 4.5839 1.26593 4.57899C1.28168 4.57408 1.29843 4.5734 1.31468 4.57701C1.67159 4.65628 2.03866 4.65646 2.39211 4.57754C2.74556 4.49862 3.07751 4.34237 3.36646 4.11889C3.91871 3.692 4.60769 3.4997 5.29912 3.57945C5.26836 3.50483 5.25625 3.42362 5.26397 3.34362C5.27169 3.26362 5.29897 3.18754 5.34319 3.12268C5.38742 3.05782 5.44709 3.00638 5.51648 2.97329C5.58586 2.94021 5.66263 2.9266 5.73938 2.93378C5.81613 2.94095 5.8903 2.96867 5.95473 3.01427C6.01917 3.05986 6.07171 3.1218 6.1073 3.19412C6.14289 3.26644 6.16033 3.34671 6.15795 3.4272C6.15557 3.5077 6.13344 3.58571 6.09369 3.65374L7.44665 9.6808C7.45269 9.70778 7.44832 9.73591 7.4345 9.75902C7.42068 9.78213 7.39854 9.79834 7.37294 9.80407L6.94265 9.90014C6.91705 9.90584 6.89009 9.9006 6.86771 9.88558C6.84533 9.87055 6.82935 9.84696 6.82328 9.81999L6.24922 7.2627C5.90143 7.19454 5.5457 7.20109 5.20373 7.28194C4.86177 7.3628 4.54076 7.51627 4.2603 7.73298C3.94924 7.97357 3.59188 8.1418 3.21137 8.22676C2.83086 8.31172 2.4357 8.31152 2.05147 8.22617C2.03261 8.22198 2.01519 8.21217 2.00132 8.19793C1.98745 8.1837 1.97772 8.16564 1.97333 8.14596L1.19982 4.70019C1.196 4.68326 1.19627 4.66569 1.20058 4.64909C1.2049 4.63249 1.21313 4.61738 1.22454 4.60514ZM5.73349 4.96552C5.30767 4.88195 4.87117 4.91064 4.46356 5.049L4.6756 5.99357C5.08247 5.85846 5.51582 5.82436 5.9419 5.89393L5.73349 4.96552ZM4.27716 5.12187C4.08781 5.20363 3.90902 5.30912 3.74463 5.43608C3.55808 5.58027 3.35433 5.69873 3.13822 5.78864L3.34958 6.73018C3.53894 6.64843 3.71772 6.54294 3.88211 6.41597C4.06866 6.27178 4.27241 6.15332 4.48852 6.06342L4.27716 5.12187ZM2.95115 5.85848C2.86709 5.88635 2.78195 5.91042 2.69567 5.92969C2.36453 6.00364 2.02186 6.01314 1.68457 5.9577L1.89306 6.88646C2.31886 6.97002 2.75535 6.94133 3.16294 6.80298L2.95115 5.85848ZM1.42677 4.80924L1.63522 5.73783C2.06101 5.82144 2.49751 5.79277 2.9051 5.65441L2.69298 4.70944C2.28616 4.84467 1.85282 4.87883 1.42677 4.80924ZM2.88044 4.6403L3.0918 5.58184C3.28117 5.50011 3.45995 5.39462 3.62433 5.26763C3.81086 5.1234 4.01461 5.00491 4.23071 4.91496L4.01928 3.97307C3.82992 4.05482 3.65114 4.16031 3.48675 4.28728C3.30024 4.43159 3.09652 4.5502 2.88044 4.6403ZM4.20569 3.90026L4.41775 4.84494C4.82462 4.7098 5.25798 4.6757 5.68405 4.7453L5.4756 3.81671C5.04979 3.73317 4.61329 3.76188 4.20569 3.90026ZM5.65235 3.14598C5.60423 3.15673 5.56056 3.18226 5.52688 3.21936C5.49319 3.25646 5.471 3.30346 5.4631 3.35441C5.4552 3.40536 5.46196 3.45797 5.48251 3.50559C5.50306 3.55322 5.53649 3.59371 5.57857 3.62196C5.62064 3.6502 5.66947 3.66493 5.71889 3.66427C5.7683 3.66362 5.81608 3.64761 5.85617 3.61828C5.89627 3.58895 5.92688 3.5476 5.94415 3.49948C5.96141 3.45135 5.96454 3.39861 5.95315 3.34791C5.93782 3.27998 5.89754 3.22057 5.84115 3.18271C5.78476 3.14485 5.71685 3.13164 5.65235 3.14598ZM7.23077 9.62232L5.92775 3.81774C5.85557 3.85867 5.77332 3.87703 5.69054 3.87071L6.99355 9.67528L7.23077 9.62232ZM6.19982 7.04266L5.99136 6.11402C5.56554 6.03045 5.12904 6.05915 4.72143 6.1975L4.93346 7.14201C5.34036 7.007 5.77377 6.97299 6.19982 7.04266ZM4.74643 7.21209L4.53502 6.27031C4.34565 6.35204 4.16687 6.45753 4.00249 6.58452C3.81595 6.72871 3.61219 6.84717 3.39608 6.93708L3.6075 7.87886C3.79686 7.79712 3.97565 7.69163 4.14002 7.56465C4.32657 7.42049 4.53035 7.30203 4.74643 7.21209ZM3.42095 7.95109L3.20899 7.00687C3.12493 7.03473 3.03981 7.05886 2.95353 7.07813C2.6224 7.15215 2.27972 7.16171 1.94242 7.10633L2.15117 8.03625C2.57705 8.11929 3.01355 8.09001 3.42095 7.95109Z" />
			</g>
			<g clipPath="url(#clip2_516_22805)">
				<path d="M12.6025 5.38228C12.593 5.36833 12.5802 5.35731 12.5653 5.35021C12.5504 5.34311 12.5339 5.34015 12.5173 5.34162C12.1514 5.37364 11.7872 5.32421 11.4484 5.19652C11.1096 5.06882 10.8037 4.86571 10.5504 4.60034C10.0665 4.0934 9.41176 3.80552 8.71395 3.79283C8.75562 3.72142 8.77978 3.64082 8.78409 3.55876C8.78839 3.47671 8.77271 3.39597 8.73854 3.32431C8.70437 3.25265 8.65286 3.19249 8.58898 3.14961C8.5251 3.10672 8.45099 3.08257 8.37377 3.07946C8.29656 3.07635 8.21885 3.0944 8.14811 3.13186C8.07738 3.16932 8.016 3.22494 7.96988 3.29337C7.92376 3.3618 7.89445 3.44073 7.88478 3.52256C7.8751 3.6044 7.88539 3.6864 7.91464 3.76066L5.67125 9.68126C5.66122 9.70776 5.66135 9.73684 5.6716 9.76212C5.68186 9.78739 5.7014 9.80679 5.72593 9.81605L6.13841 9.97151C6.16295 9.98075 6.19047 9.97908 6.21492 9.96689C6.23937 9.95469 6.25875 9.93297 6.26881 9.90648L7.22068 7.39436C7.57587 7.37234 7.92777 7.42706 8.2549 7.55516C8.58203 7.68326 8.87752 7.88207 9.12332 8.13944C9.39591 8.42513 9.72525 8.64379 10.09 8.78126C10.4548 8.91873 10.8468 8.97194 11.2407 8.93745C11.26 8.93576 11.2788 8.92818 11.2947 8.91564C11.3105 8.90309 11.3229 8.88612 11.3302 8.86679L12.6128 5.48188C12.6191 5.46525 12.6215 5.44742 12.6197 5.43003C12.6179 5.41263 12.612 5.39622 12.6025 5.38228ZM8.0758 5.13776C8.51069 5.11069 8.93941 5.19875 9.32305 5.39395L8.97147 6.32184C8.58806 6.13002 8.16328 6.03691 7.73022 6.04977L8.0758 5.13776ZM9.49706 5.49294C9.67267 5.60133 9.83425 5.73232 9.97833 5.88312C10.1418 6.05435 10.3262 6.20185 10.5272 6.32211L10.1767 7.24703C10.0011 7.13864 9.83951 7.00765 9.69543 6.85685C9.53193 6.68562 9.34753 6.53812 9.1466 6.41786L9.49706 5.49294ZM10.7023 6.41813C10.7815 6.45771 10.8624 6.4936 10.9451 6.52477C11.2625 6.64443 11.601 6.70036 11.9439 6.68981L11.5982 7.60216C11.1633 7.62923 10.7346 7.54118 10.351 7.34598L10.7023 6.41813ZM12.3713 5.56164L12.0257 6.47382C11.5908 6.50094 11.1621 6.41291 10.7785 6.2177L11.1302 5.28942C11.5135 5.48136 11.9383 5.57452 12.3713 5.56164ZM10.9546 5.19406L10.6041 6.11897C10.4285 6.01061 10.2669 5.87961 10.1229 5.7288C9.95939 5.55753 9.775 5.40999 9.57408 5.28969L9.92467 4.36443C10.1003 4.47282 10.2619 4.60381 10.4059 4.75461C10.5694 4.92596 10.7537 5.07361 10.9546 5.19406ZM9.75064 4.2655L9.39901 5.1935C9.01561 5.00165 8.59083 4.90854 8.15776 4.92143L8.5034 4.00924C8.9383 3.9822 9.367 4.07028 9.75064 4.2655ZM8.42838 3.30612C8.4745 3.3235 8.514 3.35527 8.54187 3.39739C8.56973 3.43951 8.58472 3.4901 8.58494 3.54277C8.58515 3.59543 8.57059 3.6478 8.54308 3.69325C8.51557 3.73869 8.47635 3.77519 8.43039 3.7981C8.38443 3.82102 8.33378 3.82933 8.28486 3.82199C8.23595 3.81465 8.19095 3.79198 8.15556 3.75685C8.12017 3.72173 8.09598 3.67572 8.08606 3.62465C8.07613 3.57358 8.08091 3.51975 8.09979 3.46995C8.12515 3.40323 8.17399 3.34851 8.2356 3.31779C8.2972 3.28708 8.36653 3.28288 8.42838 3.30612ZM5.89415 9.65122L8.05473 3.94917C8.1202 4.00037 8.19905 4.03009 8.28212 4.03487L6.12154 9.73692L5.89415 9.65122ZM7.30258 7.1782L7.64824 6.26596C8.08314 6.2389 8.51185 6.32696 8.8955 6.52216L8.54394 7.44999C8.16049 7.25826 7.73564 7.16524 7.30258 7.1782ZM8.71898 7.54624L9.06953 6.62109C9.24516 6.72946 9.40674 6.86045 9.5508 7.01127C9.71429 7.1825 9.8987 7.33 10.0996 7.45026L9.74908 8.37541C9.57346 8.26704 9.41188 8.13604 9.26781 7.98523C9.10431 7.81403 8.91988 7.66652 8.71898 7.54624ZM9.92332 8.47377L10.2748 7.54622C10.354 7.5858 10.4348 7.62175 10.5175 7.65292C10.8349 7.77264 11.1734 7.82864 11.5163 7.81815L11.1702 8.73164C10.7353 8.75818 10.3067 8.66952 9.92332 8.47377Z" />
			</g>
			<defs>
				<clipPath id="clip0_516_22805">
					<rect
						width="11.1751"
						height="11.1751"
						fill="white"
						transform="translate(103.824)"
					/>
				</clipPath>
				<clipPath id="clip1_516_22805">
					<rect
						width="7.21349"
						height="7.76299"
						fill="white"
						transform="matrix(-0.991477 0.130281 0.176974 0.984216 7.15234 2.32715)"
					/>
				</clipPath>
				<clipPath id="clip2_516_22805">
					<rect
						width="7.11324"
						height="7.78889"
						fill="white"
						transform="matrix(0.951579 0.307405 -0.40432 0.914618 7.20117 2.31836)"
					/>
				</clipPath>
			</defs>
		</Icon>
	)
}

export function ReactiveIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 16 16"
			fill="currentColor"
			stroke="currentColor"
			strokeWidth={0.5}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path
				d="M9.75 6.25C9.75 5.27778 8.97222 4.5 8 4.5C7.02778 4.5 6.25 5.27778 6.25 6.25C6.25 7.22222 7.02778 8 8 8C8.94444 8 9.75 7.22222 9.75 6.25ZM7.08333 6.25C7.08333 5.75 7.5 5.33333 8 5.33333C8.5 5.33333 8.91667 5.75 8.91667 6.25C8.91667 6.75 8.5 7.16667 8 7.16667C7.5 7.16667 7.08333 6.75 7.08333 6.25Z"
				fill="#CA3214"
			/>
			<path
				d="M4.5 11.9409C4.5 12.0494 4.528 12.1579 4.612 12.2393C4.696 12.3207 4.808 12.375 4.92 12.375H11.08C11.192 12.375 11.304 12.3207 11.388 12.2393C11.472 12.1579 11.5 12.0494 11.5 11.9409C11.388 10.2316 9.848 8.875 8.028 8.875C6.18 8.875 4.64 10.2316 4.5 11.9409ZM8.028 9.68895C9.26 9.68895 10.324 10.4758 10.632 11.5339H5.424C5.732 10.4758 6.768 9.68895 8.028 9.68895Z"
				fill="#CA3214"
			/>
			<path
				d="M14.3447 6.27234C14.1064 6.33191 13.9574 6.57021 14.017 6.80851C14.1064 7.19574 14.1064 7.43404 14.1064 7.88085V8C14.1064 11.366 11.366 14.1064 8 14.1064C4.63404 14.1064 1.89362 11.366 1.89362 8C1.89362 4.63404 4.63404 1.89362 8 1.89362C9.34043 1.89362 10.6213 2.34043 11.6638 3.11489H11.3362C11.0979 3.11489 10.8894 3.3234 10.8894 3.5617C10.8894 3.8 11.0979 4.00851 11.3362 4.00851H12.8255C12.9447 4.00851 13.0638 3.94894 13.1532 3.88936C13.2426 3.8 13.2723 3.68085 13.2723 3.5617V2.07234C13.2723 1.83404 13.0638 1.62553 12.8255 1.62553C12.5872 1.62553 12.3787 1.83404 12.3787 2.07234V2.54894C11.1574 1.56596 9.60851 1 8 1C4.12766 1 1 4.12766 1 8C1 11.8723 4.12766 15 8 15C11.8723 15 15 11.8723 15 8V7.91064C15 7.46383 15 7.10638 14.8809 6.62979C14.8213 6.39149 14.583 6.24255 14.3447 6.27234Z"
				fill="#CA3214"
			/>
		</Icon>
	)
}

export function CalenderCrossIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 16 16"
			fill="currentColor"
			stroke="currentColor"
			strokeWidth={0.5}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path
				d="M6.27894 11.6768C6.46857 11.8594 6.77732 11.8594 6.96938 11.6768L8.00017 10.6838L9.03097 11.6768C9.22059 11.8594 9.52934 11.8594 9.7214 11.6768C9.91346 11.4941 9.91103 11.1967 9.7214 11.0117L8.69061 10.0187L9.7214 9.02574C9.9086 8.83838 9.90374 8.54096 9.70925 8.36063C9.51962 8.18499 9.22059 8.18499 9.03097 8.36063L8.00017 9.3536L6.96938 8.36063C6.77975 8.17797 6.46857 8.17797 6.27894 8.36063C6.08931 8.5433 6.08931 8.84307 6.27894 9.02574L7.30973 10.0187L6.27894 11.014C6.08931 11.1967 6.08931 11.4941 6.27894 11.6768Z"
				fill="#CA3214"
			/>
			<path
				d="M12.6045 3.28344H11.1872V2.48017C11.1921 2.22022 10.9806 2.00476 10.7107 2.00008C10.4408 1.9954 10.2172 2.19914 10.2123 2.45909V2.48017V3.28344H5.79011V2.48017C5.79011 2.22022 5.57131 2.00945 5.30146 2.00945C5.0316 2.00945 4.81524 2.22022 4.8128 2.48017V3.28344H3.39546C2.6248 3.28344 2 3.88531 2 4.6277V12.6557C2 13.3981 2.6248 14 3.39546 14H12.6045C13.3752 14 14 13.3981 14 12.6557V4.6277C14 3.88531 13.3752 3.28344 12.6045 3.28344ZM13.0251 12.6557C13.0251 12.8806 12.8355 13.0609 12.6045 13.0609H3.39546C3.16451 13.0609 2.97488 12.8782 2.97488 12.6557V6.94852H13.0251V12.6557ZM13.0251 6.00942H2.97488V4.63004C2.97488 4.40522 3.16207 4.22489 3.39546 4.22489H4.8128V4.87125C4.8128 5.13121 5.0316 5.34198 5.30146 5.34198C5.57131 5.34198 5.78768 5.13121 5.79011 4.87125V4.22255H10.2123V4.86891C10.2075 5.12886 10.419 5.34432 10.6888 5.349C10.9587 5.35369 11.1823 5.14994 11.1872 4.88999C11.1872 4.88296 11.1872 4.87594 11.1872 4.86891V4.22255H12.6045C12.8379 4.22255 13.0251 4.40522 13.0251 4.6277V6.00942Z"
				fill="#CA3214"
			/>
		</Icon>
	)
}

export function Security(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
		</Icon>
	)
}

export function WhatsAppIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 16 16" fill="currentColor">
			<path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
		</Icon>
	)
}

export function WhatsappSolidIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 28 28" fill="currentColor">
			<path d="M12.04 2C6.55 2 2.09 6.46 2.09 11.95c0 1.76.46 3.48 1.33 5L2 22l5.19-1.36a9.9 9.9 0 0 0 4.85 1.24h.01c5.49 0 9.95-4.46 9.95-9.95S17.53 2 12.04 2Zm5.81 14.08c-.24.67-1.4 1.28-1.93 1.36-.5.08-1.13.12-1.83-.11-.42-.14-.96-.31-1.65-.61-2.91-1.26-4.8-4.21-4.95-4.41-.15-.2-1.18-1.57-1.18-2.99 0-1.42.75-2.12 1.01-2.41.26-.29.57-.36.76-.36.19 0 .38 0 .55.01.18.01.42-.07.66.51.24.58.81 1.99.88 2.14.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.32.38-.46.51-.15.15-.31.31-.13.61.18.3.81 1.34 1.74 2.17 1.2 1.07 2.2 1.4 2.51 1.56.31.16.49.13.67-.08.18-.2.78-.91.99-1.22.2-.31.41-.26.69-.16.29.1 1.82.86 2.13 1.02.31.16.51.24.58.37.07.13.07.76-.17 1.43Z" />
		</Icon>
	)
}

export function DesktopIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25"
			/>
		</Icon>
	)
}

export function GlobeIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
			/>
		</Icon>
	)
}

export function ChatIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"
			/>
		</Icon>
	)
}

export function CircleIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="12" cy="12" r="10" />
		</Icon>
	)
}
export function TravelActivitiesIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="currentColor"
			stroke="currentColor"
			strokeWidth={0.5}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path
				d="M19.2728 1.51082L13.276 0.011802V0.0177037C13.207 -0.00215773 13.1337 -0.00215773 13.0647 0.0177037L6.88531 1.94833L1.21973 0.177441C0.980628 0.102246 0.720003 0.145462 0.517963 0.293805C0.315923 0.442149 0.196643 0.677872 0.196783 0.928523V16.2354C0.195447 16.5796 0.41893 16.8843 0.747603 16.9865L6.76728 18.8675C6.8055 18.8793 6.8453 18.8853 6.88531 18.8852C6.92536 18.8851 6.96517 18.8789 7.00334 18.8668H7.00806L7.39639 18.7456C7.32789 19.0111 7.28845 19.2832 7.27875 19.5572C7.21876 21.5984 8.7304 23.3462 10.7589 23.5811C12.7875 23.816 14.6585 22.4599 15.0666 20.459L16.0108 20.4445C16.3315 22.102 17.6745 23.3698 19.3477 23.5946C21.021 23.8194 22.651 22.951 23.3977 21.4369C24.1445 19.9228 23.8415 18.1009 22.6447 16.9102L22.6557 16.8984L20.0869 14.2033C20.0722 14.1879 20.0563 14.1738 20.0393 14.1612C19.985 14.1014 19.9279 14.0431 19.8689 13.9912V2.27449C19.869 1.9133 19.6233 1.59841 19.2728 1.51082ZM0.983669 16.2354V0.928523L6.49186 2.64983V13.377H6.09842C5.88113 13.377 5.70498 13.5532 5.70498 13.7705C5.70498 13.9878 5.88113 14.1639 6.09842 14.1639H6.49186V17.9567L0.983669 16.2354ZM7.27875 17.9571V14.1639H7.67219C7.88949 14.1639 8.06564 13.9878 8.06564 13.7705C8.06564 13.5532 7.88949 13.377 7.67219 13.377H7.27875V2.64983L12.7869 0.928523V3.14754H12.3935C12.1762 3.14754 12.0001 3.32369 12.0001 3.54098C12.0001 3.75827 12.1762 3.93442 12.3935 3.93442H12.7869V13.377C12.1214 13.3798 11.4881 13.6642 11.044 14.16C11.0266 14.1731 11.0103 14.1876 10.9952 14.2033L8.43429 16.8905L8.41423 16.9098L8.42642 16.9224C8.1669 17.1853 7.94451 17.4825 7.76544 17.8056L7.27875 17.9571ZM15.9345 18.0984H15.1476V17.422L15.9345 17.6188V18.0984ZM14.3607 17.3134C13.5949 16.2827 12.3714 15.6943 11.0881 15.7397C10.9118 15.7465 10.7363 15.7659 10.5628 15.7975L11.511 14.8029C11.5403 14.783 11.5667 14.7593 11.5897 14.7325C11.8836 14.3733 12.3229 14.1647 12.7869 14.1639C13.6561 14.1639 14.3607 14.8685 14.3607 15.7377V17.3134ZM22.1261 21.8656C21.3118 22.7032 20.1005 23.0208 18.9799 22.6907C17.8594 22.3606 17.0138 21.4371 16.7835 20.2918C16.7077 19.9288 16.3864 19.6695 16.0155 19.6721H15.0666C14.696 19.6694 14.3749 19.9281 14.2986 20.2906C13.9786 21.896 12.4815 22.9882 10.855 22.8027C9.22857 22.6172 8.01584 21.2159 8.06564 19.5797C8.13262 17.9241 9.45708 16.5961 11.1125 16.5246C12.4822 16.4802 13.7225 17.3289 14.1774 18.6216C14.233 18.7788 14.3817 18.8837 14.5484 18.8837H16.5337C16.7004 18.8837 16.849 18.7788 16.9047 18.6216C17.3578 17.3275 18.5993 16.478 19.9696 16.5246C21.625 16.5961 22.9495 17.9241 23.0165 19.5797C23.0436 20.4311 22.722 21.2568 22.1261 21.8656ZM20.5193 15.7975C20.3458 15.7659 20.1703 15.7465 19.994 15.7397C18.7107 15.6941 17.4871 16.2826 16.7214 17.3134V15.7377C16.7214 14.8685 17.426 14.1639 18.2951 14.1639C18.7596 14.1644 19.1994 14.373 19.4936 14.7325C19.5155 14.7583 19.5408 14.781 19.5687 14.8001L20.5193 15.7975ZM19.082 13.5183C18.83 13.4258 18.5637 13.378 18.2951 13.377C16.9919 13.3783 15.9358 14.4345 15.9345 15.7377V16.8075L15.1476 16.6108V15.7377C15.1465 14.7383 14.5161 13.8479 13.5738 13.5148V3.93442H13.9673C14.1846 3.93442 14.3607 3.75827 14.3607 3.54098C14.3607 3.32369 14.1846 3.14754 13.9673 3.14754H13.5738V0.897441L19.082 2.27449V13.5183Z"
				fill="#8DA4EF"
			/>
			<path
				d="M3.73768 7.86879C3.95497 7.86879 4.13112 7.69264 4.13112 7.47535C4.13112 7.25806 3.95497 7.08191 3.73768 7.08191C3.52039 7.08191 3.34424 7.25806 3.34424 7.47535C3.34424 7.69264 3.52039 7.86879 3.73768 7.86879Z"
				fill="#8DA4EF"
			/>
			<path
				d="M18.295 8.65572C18.2938 7.72125 17.6366 6.91609 16.7213 6.72785V5.90162C16.7213 5.68433 16.5451 5.50818 16.3278 5.50818C16.1105 5.50818 15.9344 5.68433 15.9344 5.90162V6.72785C15.0191 6.91609 14.3618 7.72125 14.3606 8.65572C14.3606 10.3727 15.9808 12.0126 16.0496 12.0814C16.2033 12.235 16.4523 12.235 16.606 12.0814C16.6748 12.0126 18.295 10.3727 18.295 8.65572ZM16.3278 7.47539C16.9797 7.47539 17.5081 8.00384 17.5081 8.65572C17.5081 9.66136 16.7512 10.7071 16.3278 11.2131C15.9045 10.7067 15.1475 9.66097 15.1475 8.65572C15.1475 8.00384 15.6759 7.47539 16.3278 7.47539Z"
				fill="#8DA4EF"
			/>
			<path
				d="M16.328 9.04921C16.5453 9.04921 16.7215 8.87306 16.7215 8.65577C16.7215 8.43848 16.5453 8.26233 16.328 8.26233C16.1107 8.26233 15.9346 8.43848 15.9346 8.65577C15.9346 8.87306 16.1107 9.04921 16.328 9.04921Z"
				fill="#8DA4EF"
			/>
			<path
				d="M15.5411 3.93447C15.7584 3.93447 15.9346 4.11062 15.9346 4.32791C15.9346 4.5452 16.1107 4.72135 16.328 4.72135C16.5453 4.72135 16.7215 4.5452 16.7215 4.32791C16.7215 3.67603 16.193 3.14758 15.5411 3.14758C15.3239 3.14758 15.1477 3.32373 15.1477 3.54103C15.1477 3.75832 15.3239 3.93447 15.5411 3.93447Z"
				fill="#8DA4EF"
			/>
			<path
				d="M10.0328 4.72135C10.2501 4.72135 10.4263 4.5452 10.4263 4.32791C10.4263 4.11062 10.6024 3.93447 10.8197 3.93447C11.037 3.93447 11.2132 3.75832 11.2132 3.54103C11.2132 3.32373 11.037 3.14758 10.8197 3.14758C10.1679 3.14758 9.6394 3.67603 9.6394 4.32791C9.6394 4.5452 9.81555 4.72135 10.0328 4.72135Z"
				fill="#8DA4EF"
			/>
			<path
				d="M10.4263 11.4098V10.6229C10.4263 10.4056 10.2501 10.2295 10.0328 10.2295C9.81555 10.2295 9.6394 10.4056 9.6394 10.6229V11.4098C9.6394 11.6271 9.81555 11.8033 10.0328 11.8033C10.2501 11.8033 10.4263 11.6271 10.4263 11.4098Z"
				fill="#8DA4EF"
			/>
			<path
				d="M10.0328 9.44267C10.2501 9.44267 10.4263 9.26652 10.4263 9.04922V8.26234C10.4263 8.04505 10.2501 7.8689 10.0328 7.8689C9.81555 7.8689 9.6394 8.04505 9.6394 8.26234V9.04922C9.6394 9.26652 9.81555 9.44267 10.0328 9.44267Z"
				fill="#8DA4EF"
			/>
			<path
				d="M10.0328 7.08195C10.2501 7.08195 10.4263 6.9058 10.4263 6.68851V5.90162C10.4263 5.68433 10.2501 5.50818 10.0328 5.50818C9.81555 5.50818 9.6394 5.68433 9.6394 5.90162V6.68851C9.6394 6.9058 9.81555 7.08195 10.0328 7.08195Z"
				fill="#8DA4EF"
			/>
			<path
				d="M10.0329 12.5902C9.81557 12.5902 9.63942 12.7664 9.63942 12.9837C9.63942 13.2009 9.46327 13.3771 9.24598 13.3771C9.02869 13.3771 8.85254 13.5532 8.85254 13.7705C8.85254 13.9878 9.02869 14.164 9.24598 14.164C9.89786 14.164 10.4263 13.6355 10.4263 12.9837C10.4263 12.7664 10.2502 12.5902 10.0329 12.5902Z"
				fill="#8DA4EF"
			/>
			<path
				d="M4.52457 13.3771C4.30727 13.3771 4.13112 13.2009 4.13112 12.9837C4.13112 12.7664 3.95497 12.5902 3.73768 12.5902C3.52039 12.5902 3.34424 12.7664 3.34424 12.9837C3.34424 13.6355 3.87269 14.164 4.52457 14.164C4.74186 14.164 4.91801 13.9878 4.91801 13.7705C4.91801 13.5532 4.74186 13.3771 4.52457 13.3771Z"
				fill="#8DA4EF"
			/>
			<path
				d="M4.13116 11.4098V10.7803C4.51202 10.3656 5.70493 8.95119 5.70493 7.47539C5.70493 6.38893 4.82418 5.50818 3.73772 5.50818C2.65126 5.50818 1.77051 6.38893 1.77051 7.47539C1.77051 8.95041 2.96343 10.3648 3.34428 10.7803V11.4098C3.34428 11.6271 3.52043 11.8033 3.73772 11.8033C3.95501 11.8033 4.13116 11.6271 4.13116 11.4098ZM3.73772 6.29506C4.3896 6.29506 4.91805 6.82351 4.91805 7.47539C4.91805 8.48103 4.16106 9.5268 3.73772 10.0328C3.31438 9.5268 2.55739 8.48103 2.55739 7.47539C2.55739 6.82351 3.08584 6.29506 3.73772 6.29506Z"
				fill="#8DA4EF"
			/>
			<path
				d="M11.2132 17.3115C9.90944 17.3115 8.85254 18.3684 8.85254 19.6722C8.85254 20.9759 9.90944 22.0328 11.2132 22.0328C12.5169 22.0328 13.5739 20.9759 13.5739 19.6722C13.5725 18.369 12.5164 17.3128 11.2132 17.3115ZM11.2132 21.2459C10.344 21.2459 9.63942 20.5413 9.63942 19.6722C9.63942 18.803 10.344 18.0984 11.2132 18.0984C12.0824 18.0984 12.787 18.803 12.787 19.6722C12.787 20.5413 12.0824 21.2459 11.2132 21.2459Z"
				fill="#8DA4EF"
			/>
			<path
				d="M19.869 17.3115C18.5652 17.3115 17.5083 18.3684 17.5083 19.6722C17.5083 20.9759 18.5652 22.0328 19.869 22.0328C21.1727 22.0328 22.2296 20.9759 22.2296 19.6722C22.2283 18.369 21.1722 17.3128 19.869 17.3115ZM19.869 21.2459C18.9998 21.2459 18.2952 20.5413 18.2952 19.6722C18.2952 18.803 18.9998 18.0984 19.869 18.0984C20.7381 18.0984 21.4427 18.803 21.4427 19.6722C21.4427 20.5413 20.7381 21.2459 19.869 21.2459Z"
				fill="#8DA4EF"
			/>
		</Icon>
	)
}

export function ColumnsIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<rect width="18" height="18" x="3" y="3" rx="2" />
			<path d="M9 3v18" />
			<path d="M15 3v18" />{" "}
		</Icon>
	)
}

export function GripVerticalIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="9" cy="12" r="1" />
			<circle cx="9" cy="5" r="1" />
			<circle cx="9" cy="19" r="1" />
			<circle cx="15" cy="12" r="1" />
			<circle cx="15" cy="5" r="1" />
			<circle cx="15" cy="19" r="1" />
		</Icon>
	)
}
export function NewFeatureIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 52 22" fill="none">
			<path
				d="M15.8213 0.5H44C45.8996 0.5 47.2741 0.500708 48.3223 0.641602C49.3561 0.780594 49.9972 1.04799 50.4746 1.52539C50.952 2.00279 51.2194 2.64392 51.3584 3.67773C51.4993 4.7259 51.5 6.10039 51.5 8V14C51.5 15.8996 51.4993 17.2741 51.3584 18.3223C51.2194 19.3561 50.952 19.9972 50.4746 20.4746C49.9972 20.952 49.3561 21.2194 48.3223 21.3584C47.2741 21.4993 45.8996 21.5 44 21.5H15.6162C14.1603 21.5 13.5523 21.4929 12.998 21.2939C12.444 21.095 11.9707 20.7143 10.8477 19.7891L7.47852 17.0127C5.802 15.6315 4.58646 14.6287 3.77832 13.7461C2.97985 12.874 2.64087 12.1851 2.62402 11.4414C2.60729 10.6976 2.91432 9.99316 3.67285 9.08594C4.44048 8.16786 5.60996 7.1114 7.22266 5.65625L10.7969 2.43164C11.9533 1.38818 12.4411 0.958792 13.0273 0.733398C13.467 0.564415 13.9423 0.517617 14.8096 0.504883L15.8213 0.5Z"
				fill="#FFF4D5"
				stroke="#AB6400"
			></path>
			<path
				d="M26.0327 6.27273V15H24.6264L20.5142 9.0554H20.4418V15H18.8608V6.27273H20.2756L24.3835 12.2216H24.4602V6.27273H26.0327ZM27.7553 15V6.27273H33.4315V7.59801H29.3363V9.96733H33.1374V11.2926H29.3363V13.6747H33.4656V15H27.7553ZM36.881 15L34.418 6.27273H36.1183L37.6907 12.6861H37.7717L39.4506 6.27273H40.9975L42.6808 12.6903H42.7575L44.3299 6.27273H46.0302L43.5671 15H42.0075L40.2603 8.87642H40.1921L38.4407 15H36.881Z"
				fill="#AD5700"
			></path>
		</Icon>
	)
}

export function TwoFAUserIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M13.3333 8.66664C13.3333 12 11 13.6666 8.22666 14.6333C8.08143 14.6825 7.92368 14.6802 7.77999 14.6266C4.99999 13.6666 2.66666 12 2.66666 8.66664V3.99997C2.66666 3.82316 2.73689 3.65359 2.86192 3.52857C2.98694 3.40355 3.15651 3.33331 3.33332 3.33331C4.66666 3.33331 6.33332 2.53331 7.49332 1.51997C7.63456 1.39931 7.81423 1.33301 7.99999 1.33301C8.18575 1.33301 8.36542 1.39931 8.50666 1.51997C9.67332 2.53997 11.3333 3.33331 12.6667 3.33331C12.8435 3.33331 13.013 3.40355 13.1381 3.52857C13.2631 3.65359 13.3333 3.82316 13.3333 3.99997V8.66664Z"
				stroke="#60646C"
				strokeWidth="1.33"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M4.25067 12.6066C4.53515 11.8414 5.04695 11.1816 5.71735 10.7157C6.38776 10.2499 7.18466 10.0003 8.00103 10.0005C8.81741 10.0007 9.61418 10.2507 10.2843 10.7169C10.9545 11.1832 11.4659 11.8433 11.75 12.6086"
				stroke="#60646C"
				strokeWidth="1.33"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M8.00001 10.0001C9.47277 10.0001 10.6667 8.80617 10.6667 7.33341C10.6667 5.86066 9.47277 4.66675 8.00001 4.66675C6.52725 4.66675 5.33334 5.86066 5.33334 7.33341C5.33334 8.80617 6.52725 10.0001 8.00001 10.0001Z"
				stroke="#60646C"
				strokeWidth="1.33"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function CopyCheck(props: IconProps) {
	return (
		<Icon
			{...props}
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			viewBox="0 0 24 24"
		>
			<path d="m12 15 2 2 4-4" />
			<rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
			<path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
		</Icon>
	)
}

export function CodeXmlIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			viewBox="0 0 24 24"
		>
			<path d="m18 16 4-4-4-4" />
			<path d="m6 8-4 4 4 4" />
			<path d="m14.5 4-5 16" />
		</Icon>
	)
}

export function EyeIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			viewBox="0 0 24 24"
		>
			<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
			<circle cx="12" cy="12" r="3" />
		</Icon>
	)
}

export function EyeSlashIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			viewBox="0 0 24 24"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
			/>
		</Icon>
	)
}

export function ChildIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			viewBox="0 0 24 24"
		>
			<path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" />
			<path d="M15 12h.01" />
			<path d="M19.38 6.813A9 9 0 0 1 20.8 10.2a2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5s-.9 2.5-2 2.5c-.8 0-1.5-.4-1.5-1" />
			<path d="M9 12h.01" />
		</Icon>
	)
}

export function TestTubeDiagonalIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			strokeWidth="2"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M21 7 6.82 21.18a2.83 2.83 0 0 1-3.99-.01a2.83 2.83 0 0 1 0-4L17 3" />
			<path d="m16 2 6 6" />
			<path d="M12 16H4" />
		</Icon>
	)
}

export function CircleAlert(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			strokeWidth="2"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="12" cy="12" r="10" />
			<line x1="12" x2="12" y1="8" y2="12" />
			<line x1="12" x2="12.01" y1="16" y2="16" />
		</Icon>
	)
}
export function ClockCheck(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			strokeWidth="2"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M12 6v6l4 2" />
			<path d="M22 12a10 10 0 1 0-11 9.95" />
			<path d="m22 16-5.5 5.5L14 19" />
		</Icon>
	)
}
export function ScanQRCodeIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			strokeWidth="2"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M17 12v4a1 1 0 0 1-1 1h-4" />
			<path d="M17 3h2a2 2 0 0 1 2 2v2" />
			<path d="M17 8V7" />
			<path d="M21 17v2a2 2 0 0 1-2 2h-2" />
			<path d="M3 7V5a2 2 0 0 1 2-2h2" />
			<path d="M7 17h.01" />
			<path d="M7 21H5a2 2 0 0 1-2-2v-2" />
			<rect x="7" y="7" width="5" height="5" rx="1" />
		</Icon>
	)
}

export function HighDemandIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M12 16v5" />
			<path d="M16 14v7" />
			<path d="M20 10v11" />
			<path d="m22 3-8.646 8.646a.5.5 0 0 1-.708 0L9.354 8.354a.5.5 0 0 0-.707 0L2 15" />
			<path d="M4 18v3" />
			<path d="M8 14v7" />
		</Icon>
	)
}

export function DarkLogo(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 38 38"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<g clip-path="url(#clip0_40000406_98642)">
				<path
					d="M19.0155 37.3408C29.1604 37.3408 37.3845 29.1427 37.3845 19.0298C37.3845 8.91687 29.1604 0.71875 19.0155 0.71875C8.87058 0.71875 0.646484 8.91687 0.646484 19.0298C0.646484 29.1427 8.87058 37.3408 19.0155 37.3408Z"
					fill="#1A202C"
				/>
				<path
					d="M19.0538 35.5446C28.2253 35.5446 35.6603 28.1332 35.6603 18.9906C35.6603 9.84797 28.2253 2.43652 19.0538 2.43652C9.88219 2.43652 2.44727 9.84797 2.44727 18.9906C2.44727 24.7128 5.35785 29.7547 9.78517 32.7271C10.6357 33.2977 9.78517 30.9765 11.2696 27.266C12.3788 24.4936 15.0275 18.0234 15.0566 16.4954C15.0792 15.3896 14.6556 14.4386 14.2254 13.4618C14.1316 13.2523 14.0379 13.0395 13.9505 12.8267C13.1388 14.416 10.2864 16.8178 8.36546 16.4341C5.69742 15.9054 7.49551 12.759 8.60477 11.5533C8.72119 11.4244 8.84732 11.2858 8.97991 11.1375C10.0116 9.9737 11.4539 8.35214 12.7895 8.91308C13.4363 9.18387 15.0533 10.3638 16.334 11.376C16.334 11.376 16.2952 11.2761 16.2758 11.2116C15.6904 9.40954 14.8852 7.2883 18.6431 6.97559C19.5971 6.895 24.406 6.93046 23.3647 8.74222C22.9475 9.47079 21.0006 9.67388 19.4968 9.82863C18.863 9.89632 18.31 9.95435 17.9801 10.0382C18.1386 10.9054 18.2291 11.9628 18.2453 12.9267C20.4735 13.1781 21.89 12.9911 23.5555 12.7687C24.3834 12.6591 25.2727 12.5398 26.3529 12.4657C27.433 12.3883 28.9174 12.2626 29.4349 12.7816C29.7777 13.1233 29.8585 13.9712 29.7356 14.7159C29.2214 17.801 26.2461 20.4961 23.8983 22.627C23.5005 22.9881 23.1221 23.3298 22.7761 23.6586C19.3675 26.8662 17.5403 30.1642 17.7278 34.268C17.7472 34.6581 17.702 35.5253 18.1935 35.5253C18.4781 35.5414 18.7659 35.5479 19.057 35.5479L19.0538 35.5446Z"
					fill="#1A202C"
				/>
				<path
					d="M19.057 35.5837C28.2286 35.5837 35.6635 28.169 35.6603 19.0264C35.6603 9.88381 28.2221 2.47236 19.0505 2.47559C9.87896 2.47559 2.44403 9.89026 2.44727 19.0329C2.44727 24.5423 5.19939 29.6939 9.78841 32.7693C10.6389 33.3399 9.78841 31.0188 11.2728 27.3083C12.3821 24.5358 15.0307 18.0657 15.0598 16.5377C15.0792 15.4319 14.6588 14.4809 14.2287 13.5041C14.1316 13.2946 14.0411 13.0818 13.9505 12.869C13.1388 14.4583 10.2897 16.86 8.36546 16.4764C5.69742 15.9477 7.49551 12.7981 8.60477 11.5956L8.97991 11.1798C10.0148 10.016 11.4507 8.3912 12.7895 8.95214C13.4363 9.21971 15.0533 10.4028 16.334 11.4151C16.334 11.4151 16.2952 11.3184 16.2758 11.2507C15.6904 9.45182 14.8852 7.32736 18.6431 7.01465C19.5971 6.93084 24.406 6.9663 23.3647 8.78128C22.9442 9.50985 21.0006 9.71295 19.4968 9.87091C18.863 9.93861 18.3067 9.99664 17.9769 10.0805C18.1353 10.9509 18.2291 12.005 18.242 12.969C20.4703 13.2236 21.8867 13.0367 23.549 12.8142C24.3769 12.7046 25.2662 12.5853 26.3464 12.5112C27.4266 12.4338 28.9109 12.3081 29.4284 12.8271C29.7712 13.1688 29.852 14.0167 29.7291 14.7614C29.2149 17.8465 26.2429 20.5416 23.8918 22.6725C23.494 23.0336 23.1157 23.3753 22.7696 23.7041C19.361 26.9118 17.5338 30.2097 17.7181 34.3135C17.7375 34.7036 17.6955 35.5708 18.1838 35.5708C18.4717 35.5837 18.7562 35.5934 19.0473 35.5934L19.057 35.5837Z"
					fill="white"
				/>
			</g>
			<defs>
				<clipPath id="clip0_40000406_98642">
					<rect width="38" height="38" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function LinkIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			strokeWidth="2"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
			<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
		</Icon>
	)
}

export function ChevronUpIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path d="m18 15-6-6-6 6" />
		</Icon>
	)
}

export function PipetteIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="m12 9-8.414 8.414A2 2 0 0 0 3 18.828v1.344a2 2 0 0 1-.586 1.414A2 2 0 0 1 3.828 21h1.344a2 2 0 0 0 1.414-.586L15 12" />
			<path d="m18 9 .4.4a1 1 0 1 1-3 3l-3.8-3.8a1 1 0 1 1 3-3l.4.4 3.4-3.4a1 1 0 1 1 3 3z" />
			<path d="m2 22 .414-.414" />
		</Icon>
	)
}

export function MergeIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="m8 6 4-4 4 4" />
			<path d="M12 2v10.3a4 4 0 0 1-1.172 2.872L4 22" />
			<path d="m20 22-5-5" />
		</Icon>
	)
}

export function SmokingIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			stroke-width="2"
			stroke-linecap="round"
			stroke-linejoin="round"
		>
			<path d="M17 12H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h14" />
			<path d="M18 8c0-2.5-2-2.5-2-5" />
			<path d="M21 16a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
			<path d="M22 8c0-2.5-2-2.5-2-5" />
			<path d="M7 12v4" />
		</Icon>
	)
}

export function NoSmokingIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			stroke-width="2"
			stroke-linecap="round"
			stroke-linejoin="round"
		>
			<path d="M12 12H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h13" />
			<path d="M18 8c0-2.5-2-2.5-2-5" />
			<path d="m2 2 20 20" />
			<path d="M21 12a1 1 0 0 1 1 1v2a1 1 0 0 1-.5.866" />
			<path d="M22 8c0-2.5-2-2.5-2-5" />
			<path d="M7 12v4" />
		</Icon>
	)
}

export function CakeIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			stroke-width="2"
			stroke-linecap="round"
			stroke-linejoin="round"
		>
			<path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" />
			<path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" />
			<path d="M2 21h20" />
			<path d="M7 8v3" />
			<path d="M12 8v3" />
			<path d="M17 8v3" />
			<path d="M7 4h.01" />
			<path d="M12 4h.01" />
			<path d="M17 4h.01" />
		</Icon>
	)
}

export function LanguageIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			stroke-width="2"
			stroke-linecap="round"
			stroke-linejoin="round"
		>
			<path d="m5 8 6 6" />
			<path d="m4 14 6-6 2-3" />
			<path d="M2 5h12" />
			<path d="M7 2h1" />
			<path d="m22 22-5-10-5 10" />
			<path d="M14 18h6" />
		</Icon>
	)
}

export function AirportIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			stroke-width="2"
			stroke-linecap="round"
			stroke-linejoin="round"
		>
			<path d="M18.2 12.27 20 6H4l1.8 6.27a1 1 0 0 0 .95.73h10.5a1 1 0 0 0 .96-.73Z" />
			<path d="M8 13v9" />
			<path d="M16 22v-9" />
			<path d="m9 6 1 7" />
			<path d="m15 6-1 7" />
			<path d="M12 6V2" />
			<path d="M13 2h-2" />
		</Icon>
	)
}

export function UserPlusIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			stroke-width="2"
			stroke-linecap="round"
			stroke-linejoin="round"
		>
			<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<line x1="19" x2="19" y1="8" y2="14" />
			<line x1="22" x2="16" y1="11" y2="11" />
		</Icon>
	)
}

export function UserPencilIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			stroke-width="2"
			stroke-linecap="round"
			stroke-linejoin="round"
		>
			<path d="M11.5 15H7a4 4 0 0 0-4 4v2" />
			<path d="M21.378 16.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z" />
			<circle cx="10" cy="7" r="4" />
		</Icon>
	)
}

export function DataImportIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M12 15V3" />
			<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
			<path d="m7 10 5 5 5-5" />
		</Icon>
	)
}

export function MagicStickIcon(props: IconProps) {
	return (
		<Icon
			viewBox="0 0 16 16"
			fill="currentColor"
			strokeWidth={0.5}
			strokeLinecap="round"
			strokeLinejoin="round"
			{...props}
		>
			<path d="M7.38499 0.924957L7.75499 1.84496L8.67499 2.21496C8.74999 2.24496 8.79999 2.31996 8.79999 2.39996C8.79999 2.47996 8.74999 2.55496 8.67499 2.58496L7.75499 2.95496L7.38499 3.87496C7.35499 3.94996 7.27999 3.99996 7.19999 3.99996C7.11999 3.99996 7.04499 3.94996 7.01499 3.87496L6.64499 2.95496L5.72499 2.58496C5.64999 2.55496 5.59999 2.47996 5.59999 2.39996C5.59999 2.31996 5.64999 2.24496 5.72499 2.21496L6.64499 1.84496L7.01499 0.924957C7.04499 0.849957 7.11999 0.799957 7.19999 0.799957C7.27999 0.799957 7.35499 0.849957 7.38499 0.924957ZM3.56749 2.64246L4.10499 3.89496L5.35749 4.43246C5.50499 4.49496 5.59999 4.63996 5.59999 4.79996C5.59999 4.95996 5.50499 5.10496 5.35749 5.16746L4.10499 5.70496L3.56749 6.95746C3.50499 7.10496 3.35999 7.19996 3.19999 7.19996C3.03999 7.19996 2.89499 7.10496 2.83249 6.95746L2.29499 5.70496L1.04249 5.16746C0.894988 5.10496 0.799988 4.95996 0.799988 4.79996C0.799988 4.63996 0.894988 4.49496 1.04249 4.43246L2.29499 3.89496L2.83249 2.64246C2.89499 2.49496 3.03999 2.39996 3.19999 2.39996C3.35999 2.39996 3.50499 2.49496 3.56749 2.64246ZM12.4 9.19996C12.56 9.19996 12.705 9.29496 12.7675 9.44246L13.305 10.695L14.5575 11.2325C14.705 11.295 14.8 11.44 14.8 11.6C14.8 11.76 14.705 11.905 14.5575 11.9675L13.305 12.505L12.7675 13.7575C12.705 13.905 12.56 14 12.4 14C12.24 14 12.095 13.905 12.0325 13.7575L11.495 12.505L10.2425 11.9675C10.095 11.905 9.99999 11.76 9.99999 11.6C9.99999 11.44 10.095 11.295 10.2425 11.2325L11.495 10.695L12.0325 9.44246C12.095 9.29496 12.24 9.19996 12.4 9.19996ZM12.3 1.59996C12.575 1.59996 12.84 1.70996 13.0375 1.90496L14.095 2.96246C14.29 3.15996 14.4 3.42496 14.4 3.69996C14.4 3.97496 14.29 4.23996 14.095 4.43746L11.89 6.64246L9.35749 4.10996L11.5625 1.90496C11.76 1.70996 12.025 1.59996 12.3 1.59996ZM1.90499 11.5625L8.50999 4.95746L11.0425 7.48996L4.43749 14.095C4.23999 14.29 3.97499 14.4 3.69999 14.4C3.42499 14.4 3.15999 14.29 2.96249 14.095L1.90499 13.0375C1.70999 12.84 1.59999 12.575 1.59999 12.3C1.59999 12.025 1.70999 11.76 1.90499 11.5625Z" />
		</Icon>
	)
}

export function PassportIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<mask
				id="mask0_6_1885"
				maskUnits="userSpaceOnUse"
				x="2"
				y="0"
				width="21"
				height="22"
			>
				<path d="M2 0.764709H23V21.7647H2V0.764709Z" fill="white" />
			</mask>
			<g mask="url(#mask0_6_1885)">
				<path
					d="M17.3724 21.1495H5.37427V4.18047H17.3724C18.6169 4.18047 19.6258 5.17289 19.6258 6.39705V18.9329C19.6258 20.1571 18.6169 21.1495 17.3724 21.1495Z"
					stroke="#1C2024"
					stroke-width="1.5"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M5.37427 4.18048L14.773 1.78581C16.2644 1.35337 17.7622 2.66642 17.5715 4.18048"
					stroke="#1C2024"
					stroke-width="1.5"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M17.0825 11.3921C17.0825 14.0826 15.0309 16.2637 12.5 16.2637C9.96912 16.2637 7.91748 14.0826 7.91748 11.3921C7.91748 8.70168 9.96912 6.52061 12.5 6.52061C15.0309 6.52061 17.0825 8.70168 17.0825 11.3921Z"
					stroke="#1C2024"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M12.1096 7.28422C10.4086 10.6138 10.3954 12.9101 12.0822 16.2264"
					stroke="#1C2024"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M12.9092 16.2264C14.5975 12.9045 14.5982 10.6082 12.8987 7.28416"
					stroke="#1C2024"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M8.05347 11.7559H16.9386"
					stroke="#1C2024"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M8.08594 18.7245H10.2133"
					stroke="#1C2024"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M12.6785 18.7245H17.0826"
					stroke="#1C2024"
					stroke-miterlimit="10"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</g>
		</Icon>
	)
}

export function PassportExpiryIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
		>
			<path
				d="M8 7V3M16 7V3M5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21Z"
				stroke="#1C2024"
				stroke-width="1.5"
				stroke-linecap="round"
				stroke-linejoin="round"
			/>
			<path
				d="M12.0007 12.4999V13.4999M12.0007 15.4999H12.0057M8.53669 17.4999H15.4647C16.2347 17.4999 16.7157 16.6664 16.3307 15.9999L12.8667 9.99988C12.4817 9.33337 11.5197 9.33337 11.1347 9.99988L7.67069 15.9999C7.28569 16.6664 7.76669 17.4999 8.53669 17.4999Z"
				stroke="#1C2024"
				stroke-linecap="round"
				stroke-linejoin="round"
			/>
		</Icon>
	)
}

export function FlightTicketIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M10.5 17h1.227a2 2 0 0 0 1.345-.52L18 12" />
			<path d="m12 13.5 3.794.506" />
			<path d="m3.173 8.18 11-5a2 2 0 0 1 2.647.993L18.56 8" />
			<path d="M6 10V8" />
			<path d="M6 14v1" />
			<path d="M6 19v2" />
			<rect x="2" y="8" width="20" height="13" rx="2" />
		</Icon>
	)
}

export function NewBadgeIcon(props: IconProps) {
	const uid = useId()
	const clipId = `newBadgeClip-${uid}`
	const glowId = `starGlow-${uid}`
	const shineId = `shineGrad-${uid}`
	return (
		<Icon
			size="12"
			height="5"
			{...props}
			viewBox="0 0 530 200"
			xmlns="http://www.w3.org/2000/svg"
		>
			<defs>
				<clipPath id={clipId}>
					<rect x="0" y="0" width="530" height="200" rx="100" ry="100" />
				</clipPath>

				<radialGradient id={glowId} cx="50%" cy="50%" r="50%">
					<stop offset="0%" stopColor="#FFEBA8" stopOpacity="0.95" />
					<stop offset="55%" stopColor="#FFD24D" stopOpacity="0.45" />
					<stop offset="100%" stopColor="#FFD24D" stopOpacity="0" />
				</radialGradient>

				<linearGradient id={shineId} x1="0" y1="0" x2="1" y2="0">
					<stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
					<stop offset="50%" stopColor="#ffffff" stopOpacity="0.55" />
					<stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
				</linearGradient>
			</defs>

			<g clipPath={`url(#${clipId})`}>
				<rect
					x="0"
					y="0"
					width="530"
					height="200"
					rx="100"
					ry="100"
					fill="#108906"
				/>

				<circle
					className={newBadgeStarGlow}
					cx="104"
					cy="100"
					r="82"
					fill="url(#starGlow)"
				/>

				<polygon
					className={newBadgeMainStar}
					points="104,38 118.1,80.6 163,80.8 126.8,107.4 140.4,150.2 104,124 67.6,150.2 81.2,107.4 45,80.8 89.9,80.6"
					fill="#FEC93B"
					stroke="#FEC93B"
					strokeWidth="6"
					strokeLinejoin="round"
				/>

				<text
					x="210"
					y="145"
					fontFamily="Arial, Helvetica, sans-serif"
					fontWeight="800"
					fontSize="112"
					fill="#ffffff"
					letterSpacing="2"
				>
					NEW
				</text>

				<g
					className={newBadgeRays}
					stroke="#ffffff"
					strokeWidth="9"
					strokeLinecap="round"
				>
					<line x1="128" y1="58" x2="158" y2="26" />
					<line x1="80" y1="58" x2="50" y2="26" />
					<line x1="104" y1="158" x2="104" y2="196" />
				</g>

				<polygon
					className={newBadgeSparkleLeft}
					fill="#ffffff"
					points="29,111 31.5,121.5 42,124 31.5,126.5 29,137 26.5,126.5 16,124 26.5,121.5"
				/>

				<polygon
					className={newBadgeSparkleRight}
					fill="#ffffff"
					points="172,129 174.1,137.9 183,140 174.1,142.1 172,151 169.9,142.1 161,140 169.9,137.9"
				/>

				<rect
					className={newBadgeShine}
					x="-80"
					y="-60"
					width="90"
					height="320"
					fill={`url(#${shineId})`}
				/>
			</g>
		</Icon>
	)
}

export function LocateFixedIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<line x1="2" x2="5" y1="12" y2="12" />
			<line x1="19" x2="22" y1="12" y2="12" />
			<line x1="12" x2="12" y1="2" y2="5" />
			<line x1="12" x2="12" y1="19" y2="22" />
			<circle cx="12" cy="12" r="7" />
			<circle cx="12" cy="12" r="3" />
		</Icon>
	)
}

export function MapPinnedIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M18 8c0 3.613-3.869 7.429-5.393 8.795a1 1 0 0 1-1.214 0C9.87 15.429 6 11.613 6 8a6 6 0 0 1 12 0" />
			<circle cx="12" cy="8" r="2" />
			<path d="M8.714 14h-3.71a1 1 0 0 0-.948.683l-2.004 6A1 1 0 0 0 3 22h18a1 1 0 0 0 .948-1.316l-2-6a1 1 0 0 0-.949-.684h-3.712" />
		</Icon>
	)
}

export function ArrowUpDownIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="m3 16 4 4 4-4" />
			<path d="M7 20V4" />
			<path d="m21 8-4-4-4 4" />
			<path d="M17 4v16" />
		</Icon>
	)
}

export function CheckListIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M13 5h8" />
			<path d="M13 12h8" />
			<path d="M13 19h8" />
			<path d="m3 17 2 2 4-4" />
			<path d="m3 7 2 2 4-4" />
		</Icon>
	)
}

export function VisaIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 28 28"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
		>
			<path
				d="M20.4965 27.1797H4.49902V4.55434H20.4965C22.1559 4.55434 23.501 5.87755 23.501 7.50978V24.2242C23.501 25.8565 22.1559 27.1797 20.4965 27.1797Z"
				strokeMiterlimit="10"
			/>
			<path
				d="M4.49902 4.55435L17.0306 1.36145C19.0192 0.78487 21.0163 2.5356 20.762 4.55435"
				strokeMiterlimit="10"
			/>
			<path
				d="M20.1097 14.17C20.1097 17.7573 17.3742 20.6654 13.9997 20.6654C10.6252 20.6654 7.88965 17.7573 7.88965 14.17C7.88965 10.5827 10.6252 7.67457 13.9997 7.67457C17.3742 7.67457 20.1097 10.5827 20.1097 14.17Z"
				strokeMiterlimit="10"
			/>
			<path
				d="M13.4798 8.69272C11.2119 13.1321 11.1942 16.1939 13.4432 20.6157"
				strokeMiterlimit="10"
			/>
			<path
				d="M14.5452 20.6156C16.7964 16.1864 16.7972 13.1247 14.5312 8.69261"
				strokeMiterlimit="10"
			/>
			<path d="M8.07129 14.6548H19.9182" strokeMiterlimit="10" />
			<path d="M8.11426 23.9465H10.9508" strokeMiterlimit="10" />
			<path d="M14.2383 23.9465H20.1105" strokeMiterlimit="10" />
		</Icon>
	)
}

export function GlobeWithBottomLineIcon(props: IconProps) {
	const clipId = useId()
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
		>
			<g clipPath={`url(#${clipId})`}>
				<path d="M16.5 18.75C16.5 19.164 16.1648 19.5 15.75 19.5H8.25C7.83525 19.5 7.5 19.164 7.5 18.75C7.5 18.336 7.83525 18 8.25 18H15.75C16.1648 18 16.5 18.336 16.5 18.75ZM18.75 0.75H5.25C4.0125 0.75 3 1.7625 3 3V21C3 22.2375 4.0125 23.25 5.25 23.25H18.75C19.9875 23.25 21 22.2375 21 21V3C21 1.7625 19.9875 0.75 18.75 0.75ZM12 15.75C8.6925 15.75 6 13.0575 6 9.75C6 6.4425 8.6925 3.75 12 3.75C15.3075 3.75 18 6.4425 18 9.75C18 13.0575 15.3075 15.75 12 15.75ZM14.355 5.925C14.7 6.81 14.91 7.89 14.9775 9H16.4325C16.215 7.7025 15.435 6.5925 14.355 5.925ZM14.355 13.575C15.435 12.9075 16.215 11.7975 16.4332 10.5H14.9783C14.91 11.61 14.7 12.69 14.355 13.575ZM12 5.25C11.52 5.25 10.6875 6.66 10.53 9H13.47C13.3125 6.66 12.48 5.25 12 5.25ZM12 14.25C12.48 14.25 13.3125 12.84 13.47 10.5H10.53C10.6875 12.84 11.52 14.25 12 14.25ZM7.5675 9H9.0225C9.09 7.89 9.3 6.81 9.645 5.925C8.565 6.5925 7.785 7.7025 7.5675 9ZM7.5675 10.5C7.785 11.7975 8.565 12.9075 9.64575 13.575C9.30075 12.69 9.09075 11.61 9.02325 10.5H7.5675ZM14.9775 10.5C14.91 11.61 14.7 12.69 14.355 13.575C15.435 12.9075 16.215 11.7975 16.4332 10.5H14.9775ZM9.0225 10.5H7.5675C7.785 11.7975 8.565 12.9075 9.64575 13.575C9.3 12.69 9.09 11.61 9.0225 10.5ZM7.5675 9H9.0225C9.09 7.89 9.3 6.81 9.645 5.925C8.565 6.5925 7.785 7.7025 7.5675 9ZM12 5.25C11.52 5.25 10.6875 6.66 10.53 9H13.47C13.3125 6.66 12.48 5.25 12 5.25ZM10.53 10.5C10.6875 12.84 11.52 14.25 12 14.25C12.48 14.25 13.3125 12.84 13.47 10.5H10.53ZM14.355 5.925C14.7 6.81 14.91 7.89 14.9775 9H16.4325C16.215 7.7025 15.435 6.5925 14.355 5.925ZM16.4325 9C16.215 7.7025 15.435 6.5925 14.355 5.925C14.7 6.81 14.91 7.89 14.9775 9H16.4325ZM14.355 13.575C15.435 12.9075 16.215 11.7975 16.4332 10.5H14.9783C14.91 11.61 14.7 12.69 14.355 13.575ZM12 5.25C11.52 5.25 10.6875 6.66 10.53 9H13.47C13.3125 6.66 12.48 5.25 12 5.25ZM12 14.25C12.48 14.25 13.3125 12.84 13.47 10.5H10.53C10.6875 12.84 11.52 14.25 12 14.25ZM7.5675 9H9.0225C9.09 7.89 9.3 6.81 9.645 5.925C8.565 6.5925 7.785 7.7025 7.5675 9ZM7.5675 10.5C7.785 11.7975 8.565 12.9075 9.64575 13.575C9.30075 12.69 9.09075 11.61 9.02325 10.5H7.5675Z" />
			</g>
			<defs>
				<clipPath id={clipId}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function StickyNoteIcon(props: IconProps) {
	const maskId = useId()
	return (
		<Icon {...props} viewBox="96 96 448 448">
			<defs>
				<mask id={maskId}>
					<path
						d="M160 96H480C515.3 96 544 124.7 544 160V373.5L373.5 544H160C124.7 544 96 515.3 96 480V160C96 124.7 124.7 96 160 96Z"
						fill="white"
					/>
					<path
						d="M485.5 368H392C378.7 368 368 378.7 368 392V485.5L485.5 368Z"
						fill="black"
					/>
				</mask>
			</defs>
			<path
				d="M160 96H480C515.3 96 544 124.7 544 160V373.5L373.5 544H160C124.7 544 96 515.3 96 480V160C96 124.7 124.7 96 160 96Z"
				fill="currentColor"
				mask={`url(#${maskId})`}
			/>
		</Icon>
	)
}

export function AnimatedOkIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			className={checkmark}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 52 52"
		>
			<circle
				className={checkmarkCircle}
				cx="26"
				cy="26"
				r="25"
				fill="#fff"
				stroke="#4bb71b"
				strokeWidth="2"
				strokeDasharray={166}
				strokeDashoffset={166}
			/>
			<path
				className={checkmarkCheck}
				d="M14.1 27.2l7.1 7.2 16.7-16.8"
				fill="none"
				stroke="#4bb71b"
				strokeWidth="2"
				strokeDasharray={48}
				strokeDashoffset={48}
			/>
		</Icon>
	)
}

export function PlayIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24">
			<path
				fill-rule="evenodd"
				d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
				clip-rule="evenodd"
			/>
		</Icon>
	)
}

export function PauseIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24">
			<path
				fill-rule="evenodd"
				d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25Zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25Z"
				clip-rule="evenodd"
			/>
		</Icon>
	)
}

export function OutgoingCallIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M7 7h10v10" />
			<path d="M7 17 17 7" />
		</Icon>
	)
}

export function IncomingCallIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M17 7 7 17" />
			<path d="M17 17H7V7" />
		</Icon>
	)
}

export function MissedCallIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 14 14"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<g clip-path="url(#clip0_129_4353)">
				<path
					d="M7.00049 10.1646L2.91715 6.08126V8.75001H1.75049V4.08334H6.41715V5.25001H3.73382L7.00049 8.51668L11.4338 4.08334L12.2505 4.91459L7.00049 10.1646Z"
					fill="currentColor"
				/>
			</g>
			<defs>
				<clipPath id="clip0_129_4353">
					<rect width="14" height="14" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function OutbundMissedCallIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			viewBox="0 0 18 18"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M9 12.1646L3.75 6.91459L4.56667 6.08334L9 10.5167L12.2667 7.25001H9.58333V6.08334H14.25V10.75H13.0833V8.08126L9 12.1646Z"
				fill="currentColor"
			/>
		</Icon>
	)
}

export function MessageIcon(props: IconProps) {
	return (
		<Icon
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
			<path d="M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1" />
		</Icon>
	)
}

export default {
	Airplane: AirplaneIcon,
	Annotation: AnnotationIcon,
	ArrowExpand: ArrowExpandIcon,
	ArrowLeft: ArrowLeftIcon,
	ArrowDownLeft: ArrowDownLeftIcon,
	ArrowRightLeft: ArrowRightLeftIcon,
	ArrowTopRightOnSquare: ArrowTopRightOnSquareIcon,
	Attention: AttentionIcon,
	AttentionSolid: AttentionSolidIcon,
	BadgeCheckSolid: BadgeCheckSolidIcon,
	Baggage: BaggageIcon,
	Ban: BanIcon,
	BankNotes: BankNotesIcon,
	Bed: BedIcon,
	Bell: BellIcon,
	BellSlash: BellSlashIcon,
	BellAlertSolid: BellAlertSolidIcon,
	SpeakerWave: SpeakerWaveIcon,
	SpeakerXMark: SpeakerXMarkIcon,
	Briefcase: BriefcaseIcons,
	Bus: BusIcon,
	Building: BuildingIcon,
	CircleArrow: CircleArrowIcon,
	Calendar: CalendarIcon,
	Cancel: CancelIcon,
	CancelCircleSolid: CancelCircleSolidIcon,
	ChevronDown: ChevronDownIcon,
	ChevronUp: ChevronUpIcon,
	ChevronRight: ChevronRightIcon,
	ChevronLeft: ChevronLeftIcon,
	ClipboardCopy: ClipboardCopyIcon,
	CopyCheck: CopyCheck,
	Clock: ClockIcon,
	ClockSolid: ClockSolidIcon,
	ClockCheck: ClockCheck,
	Cog: CogIcon,
	CodeXml: CodeXmlIcon,
	Eye: EyeIcon,
	EyeSlash: EyeSlashIcon,
	Document: DocumentIcon,
	DocumentDownload: DocumentDownloadIcon,
	DotsVertical: DotsVerticalIcon,
	Driver: DriverIcon,
	Duplicate: DuplicateIcon,
	FingerPrint: FingerPrintIcon,
	Flag: FlagIcon,
	FlagSolid: FlagSolidIcon,
	Global: GlobalIcon,
	HandPencil: HandPencilIcon,
	HandShake: HandShakeIcon,
	Service: ServiceIcon,
	Home: HomeIcon,
	HandRaised: HandRaisedIcon,
	HandThumbUp: HandThumbUpIcon,
	HandThumbUpSolid: HandThumbUpSolidIcon,
	Identification: IdentificationIcon,
	InboxIn: InboxInIcon,
	Info: InfoIcon,
	InfoSolid: InfoSolidIcon,
	LifeBouy: LifeBouyIcon,
	LightBulb: LightBulbIcon,
	LightningBolt: LightningBoltIcon,
	LightningBoltSolid: LightningBoltSolidIcon,
	LightFlag: LightFlagIcon,
	LocationMarker: LocationMarkerIcon,
	Login: LoginIcon,
	Logout: LogoutIcon,
	Lock: LockIcon,
	Meal: MealIcon,
	Mail: MailIcon,
	Map: MapIcon,
	Markdown: MarkdownIcon,
	MinusCircle: MinusCircleIcon,
	Ok: OkIcon,
	OkCircleSolid: OkCircleSolidIcon,
	OkCircle: OkCircleIcon,
	PaperAirplane: PaperAirplaneIcon,
	PaperClip: PaperClipIcon,
	Pencil: PencilIcon,
	Phone: PhoneIcon,
	Mobile: MobileIcon,
	Photograph: PhotographIcon,
	Plus: PlusIcon,
	PlusCircle: PlusCircleIcon,
	Preview: PreviewIcon,
	QuestionMarkCircle: QuestionMarkCircleIcon,
	RectangleStack: RectangleStackIcon,
	Refresh: RefreshIcon,
	Receipt: ReceiptIcon,
	Reply: ReplyIcon,
	Rupee: RupeeIcon,
	Search: SearchIcon,
	Share: ShareIcon,
	Sheet: SheetIcon,
	ShieldCheck: ShieldCheckIcon,
	EmptyShield: EmptyShieldIcon,
	ShieldExclamation: ShieldExclamationIcon,
	HorizontalSliders: HorizontalSlidersIcon,
	Sliders: SlidersIcon,
	KeyRound: KeyRoundLuciode,
	SparklesSolid: SparklesSolidIcon,
	Spinner: SpinnerIcon,
	Star: StarIcon,
	StarEmpty: StarEmptyIcon,
	SteeringWheel: SteeringWheelIcon,
	Support: SupportIcon,
	SwitchHorizontal: SwitchHorizontalIcon,
	Tags: TagsIcon,
	Taxi: TaxiIcon,
	Ticket: TicketIcon,
	Trash: TrashIcon,
	Upload: UploadIcon,
	User: UserIcon,
	Adult: AdultIcon,
	UserRound: UserRoundIcon,
	TwoFAUser: TwoFAUserIcon,
	UserGroup: UserGroupIcon,
	Users: UsersIcon,
	UnLock: UnLockIcon,
	ViewList: ViewListIcon,
	RefreshClock: RefreshClockIcon,
	History: RefreshClockIcon,
	Reactive: ReactiveIcon,
	CalenderCross: CalenderCrossIcon,
	Security: Security,
	Desktop: DesktopIcon,
	Globe: GlobeIcon,
	GlobeWithBottomLine: GlobeWithBottomLineIcon,
	Chat: ChatIcon,
	CheckCheck: CheckCheckIcon,
	Circle: CircleIcon,
	WhatsApp: WhatsAppIcon,
	WhatsAppSolid: WhatsappSolidIcon,
	XCircle: XCircleIcon,
	CircleAlert: CircleAlert,
	Columns: ColumnsIcon,
	GripVertical: GripVerticalIcon,
	NewFeature: NewFeatureIcon,
	TravelActivitie: TravelActivitiesIcon,
	TestTubeDiagonal: TestTubeDiagonalIcon,
	DocumentText: DocumentTextIcon,
	Child: ChildIcon,
	Link: LinkIcon,
	ScanQRCode: ScanQRCodeIcon,
	HighDemand: HighDemandIcon,
	Visa: VisaIcon,
	CheckList: CheckListIcon,
	ArrowUpDown: ArrowUpDownIcon,
	MapPinned: MapPinnedIcon,
	LocateFixed: LocateFixedIcon,
	Passport: PassportIcon,
	Pipette: PipetteIcon,
	Merge: MergeIcon,
	Smoking: SmokingIcon,
	NoSmoking: NoSmokingIcon,
	Cake: CakeIcon,
	Language: LanguageIcon,
	Airport: AirportIcon,
	UserPlus: UserPlusIcon,
	UserPencil: UserPencilIcon,
	DataImport: DataImportIcon,
	DarkLogo: DarkLogo,
	MagicStick: MagicStickIcon,
	PassportExpiry: PassportExpiryIcon,
	FlightTicket: FlightTicketIcon,
	NewBadge: NewBadgeIcon,
	Play: PlayIcon,
	Pause: PauseIcon,
	IncomingCall: IncomingCallIcon,
	OutgoingCall: OutgoingCallIcon,
	MissedCall: MissedCallIcon,
	OutboundMissedCall: OutbundMissedCallIcon,
	Message: MessageIcon,
	StickyNote: StickyNoteIcon,
	AnimatedOk: AnimatedOkIcon,
}
